import {
  Badge,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Checkbox,
  Textarea,
  Th,
  Thead,
  Tr,
  UnorderedList,
  useToast,
  VStack
} from "@chakra-ui/react";
import '../common.css';
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import ReadOnlyExemptButton from "../../components/ReadOnlyExemptButton";
import { useTranslation } from "react-i18next";
import { FaRegQuestionCircle } from "react-icons/fa";
import { RiSparklingFill } from "react-icons/ri";
import { FiChevronDown, FiChevronUp, FiTrash2 } from "react-icons/fi";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import { codeToNameMap, ShiftSystem } from "../../contexts/constants";
import {
  Currency,
  CycleTimeInSecondByPartNoType,
  EquipmentType,
  Errors,
  PagePermissions,
  ProductionCapacityData,
  RationalizationProfitLossItem,
  RationalizationProfitLossItemType,
  UniqueProcessType,
  UpdateSummaryInputData,
  ProjectWithRdsData,
} from "../../contexts/types";
import { formatNumber, hasDecimal, hourMinutesStringToMinutes, resolveDisplayPartNoByKey } from "../../utils/utils";
import { getMultilingualDisplayName } from "../../utils/i18nDisplay";
import { calcEquipmentDepreciationByYearMonth, convertEquipmentData } from "../../utils/calculation";
import { WebAPIContext } from "../../contexts/WebAPIContext";
import { useAppSelector } from "../../store/hooks";
import CeasTimePicker from "../InvestmentScreening/CeasTimePicker";
import AIAnalysisModal from "./AIAnalysisModal";
import { generateUpdateSummaryPrompt, generateManufacturingKnowledgePrompt, AI_ANALYSIS_TITLES } from "../../utils/aiPrompt";
import GoogleAPI from "../../utils/GoogleAPI";

/**
 * 指標グループの項目選択肢（value/unit のみ保持）
 */
const INDICATOR_ITEM_OPTIONS_BASE: { value: RationalizationProfitLossItemType | ""; unit: "pcs" | "currency" }[] = [
  { value: "", unit: "pcs" },
  { value: "productionQuantity", unit: "pcs" },
  { value: "salesQuantity", unit: "pcs" },
  { value: "custom", unit: "currency" },
];

/**
 * 費用グループの項目選択肢（value/unit のみ保持）
 */
const COST_ITEM_OPTIONS_BASE: { value: RationalizationProfitLossItemType | ""; unit: "pcs" | "currency" }[] = [
  { value: "", unit: "pcs" },
  { value: "laborCost", unit: "currency" },
  { value: "repairCost", unit: "currency" },
  { value: "consumablesCost", unit: "currency" },
  { value: "maintenanceCost", unit: "currency" },
  { value: "scrapCost", unit: "currency" },
  { value: "targetEquipmentDepreciation", unit: "currency" },
  { value: "otherDepreciation", unit: "currency" },
  { value: "custom", unit: "currency" },
];

/** 削除不可の必須指標項目タイプ（簡易モード時のみ必須） */
const REQUIRED_INDICATOR_ITEM_TYPES_SIMPLE_ONLY: RationalizationProfitLossItemType[] = [
  "salesQuantity",
];

/** 削除不可の必須費用項目タイプ（詳細・簡易共通） */
const REQUIRED_COST_ITEM_TYPES_COMMON: RationalizationProfitLossItemType[] = [
  "targetEquipmentDepreciation",
  "otherDepreciation",
];

/** 削除不可の必須費用項目タイプ（簡易モード時のみ必須） */
const REQUIRED_COST_ITEM_TYPES_SIMPLE_ONLY: RationalizationProfitLossItemType[] = [
  "laborCost",
];

/** 項目タイプからデフォルト単位種別を判定 */
const getDefaultUnitTypeByItemType = (itemType: RationalizationProfitLossItemType): "pcs" | "currency" => {
  if (itemType === "productionQuantity" || itemType === "salesQuantity") return "pcs";
  return "currency";
};

/** 項目型（label 付き） */
type ItemOption = { value: RationalizationProfitLossItemType | ""; label: string; unit: "pcs" | "currency" };

/**
 * 更新前後サマリーPopoverのプロパティ
 */
interface Props {
  /** プロジェクトID */
  projectId: string;
  /** ラインID（工程ID） */
  productionLineId?: string;
  /** ライン名（工程名） */
  productionLineName?: string;
  /** 既存の更新前後サマリーデータ */
  updateSummaryData?: UpdateSummaryInputData | null;
  /** プロセスコードデータ */
  processCodeData: UniqueProcessType[];
  /** 保存ボタン活性制御 */
  isSave: boolean;
  /** 保存ハンドラー */
  onSave: (data: UpdateSummaryInputData | undefined) => Promise<boolean>;
  /** 画面パーミッション */
  pagePermissions?: PagePermissions;
  /** ライン情報 */
  productionLineData?: any;
  /** 設備データ */
  equipmentData?: EquipmentType[];
  /** プロジェクト基本情報（AI製造ナレッジ検索用） */
  projectWithRdsData?: ProjectWithRdsData;
  /** パーツNo文字列リスト（AI製造ナレッジ検索用） */
  partNoStrings?: string[];
  /** PartNoデータ（displayPartNo解決用、No.435対応） */
  partNoData?: { partNo: string; displayPartNo?: string }[];
  /** 量産開始日（YYYY-MM-DD形式） */
  projectStartDate?: string | null;
  /** EOL予定日（YYYY-MM-DD形式） */
  projectEndDate?: string | null;
  /** ローカル通貨コード */
  localCurrency?: string | null;
  /** 通貨データ */
  currencyData?: Currency[];
  /** 簡易モードか（true時のみ指標・費用グループを表示） */
  isSimpleMode?: boolean;
}

/**
 * 更新前後サマリーPopover
 */
const UpdateSummaryInputPopover: React.FC<Props> = ({
  projectId,
  productionLineId,
  productionLineName,
  updateSummaryData,
  processCodeData,
  isSave,
  onSave,
  pagePermissions,
  productionLineData,
  equipmentData,
  projectWithRdsData,
  partNoStrings,
  partNoData,
  projectStartDate,
  projectEndDate,
  localCurrency = "JPY",
  currencyData = [],
  isSimpleMode = false,
}) => {

  const { t } = useTranslation();

  /** 現在のモードで必須となる費用項目タイプ */
  const requiredCostItemTypes: RationalizationProfitLossItemType[] = isSimpleMode
    ? [...REQUIRED_COST_ITEM_TYPES_COMMON, ...REQUIRED_COST_ITEM_TYPES_SIMPLE_ONLY]
    : REQUIRED_COST_ITEM_TYPES_COMMON;

  /** 現在のモードで必須となる指標項目タイプ */
  const requiredIndicatorItemTypes: RationalizationProfitLossItemType[] = isSimpleMode
    ? REQUIRED_INDICATOR_ITEM_TYPES_SIMPLE_ONLY
    : [];

  /** 必須費用項目を補完する */
  const ensureRequiredCostItems = (items: RationalizationProfitLossItem[]): RationalizationProfitLossItem[] => {
    const result = [...items];
    requiredCostItemTypes.forEach((type) => {
      if (!result.some((item) => item.itemType === type)) {
        result.push({
          id: uuidv4(),
          itemType: type,
          unitType: "currency",
          beforeMonthlyData: {},
          afterMonthlyData: {},
        });
      }
    });
    return result;
  };

  /** 必須指標項目を補完する */
  const ensureRequiredIndicatorItems = (items: RationalizationProfitLossItem[]): RationalizationProfitLossItem[] => {
    const result = [...items];
    requiredIndicatorItemTypes.forEach((type) => {
      if (!result.some((item) => item.itemType === type)) {
        result.push({
          id: uuidv4(),
          itemType: type,
          unitType: getDefaultUnitTypeByItemType(type),
          beforeMonthlyData: {},
          afterMonthlyData: {},
        });
      }
    });
    return result;
  };
  const defaultUpdateSummary: UpdateSummaryInputData = {
    productionLineId: productionLineId,
    productionLineName: productionLineName,
    fixedAssetNumber: "",
    operatingDays: 0,
    workTime: "00:00",
    shiftSystem: ShiftSystem.OneShiftSystem.code,
    partsCount: 1,
    cumulativeCost: 0,
    selectedPartNo: "",
    productionCapacityByPartNo: {},
    updateReason: "",
    productionPerEquipment: "",
    productionPerPerson: "",
    nonOperationMeasures: "",
    nonProcessingMeasures: "",
    currentEquipmentHandling: "",
    profitLoss: {
      productionQuantity: 0,
      recentActual: {
        scrapRate: undefined,
        scrapCost: undefined,
        repairCost: 0,
        consumablesCost: 0,
      },
      theoretical: {
        scrapRate: undefined,
        scrapCost: undefined,
        repairCost: 0,
        consumablesCost: 0,
      },
      investmentPlan: {
        scrapRate: undefined,
        scrapCost: undefined,
        repairCost: 0,
        consumablesCost: 0,
      },
    },
  };

  /** デフォルトの生産能力データ */
  const defaultProductionCapacityData: ProductionCapacityData = {
    yield: 0,
    nonWorkingTime: 0,
    machineTrouble: 0,
    maintenance: 0,
    operatingRate: undefined,
    cycleTime: 0,
    personnelPerDay: 0,
    productionVolume: undefined,
    productionPerEquipment: undefined,
    productionPerPerson: undefined,
  };
  /** ローカル更新前後サマリーデータ */
  const [localUpdateSummary, setLocalUpdateSummary] = useState<UpdateSummaryInputData>(defaultUpdateSummary);
  const TITLE = t('components.UpdateSummaryInputPopover.title')

  /** 通信オブジェクト */
  const WebAPI = useContext(WebAPIContext);

  /** GoogleAPI インスタンス */
  const googleAPI: GoogleAPI = new GoogleAPI();
  /** SpreadSheet保存パス */
  const SPREAD_SHEET_PATH = "CEAS/SpreadSheet/UpdateSummary";

  /** 年月リストを生成（量産開始日～EOL予定日） */
  const monthList = useMemo((): string[] => {
    if (!projectStartDate || !projectEndDate) return [];
    const start = dayjs(projectStartDate).startOf("month");
    const end = dayjs(projectEndDate).startOf("month");
    if (!start.isValid() || !end.isValid() || start.isAfter(end)) return [];
    const months: string[] = [];
    let current = start;
    while (current.isBefore(end) || current.isSame(end, "month")) {
      months.push(current.format("YYYY-MM"));
      current = current.add(1, "month");
    }
    return months;
  }, [projectStartDate, projectEndDate]);

  /** 年月を「YYYY年M月」形式でフォーマット */
  const formatMonthDisplay = (month: string): string => {
    const d = dayjs(month);
    if (!d.isValid()) return month;
    return d.format(t('common.dateFormat.dayjsYearMonth'));
  };

  /** 指標グループの項目選択肢（翻訳付き） */
  const INDICATOR_ITEM_OPTIONS = useMemo((): ItemOption[] => [
    { value: "", label: t('components.UpdateSummaryInputPopover.options.indicator.selectPlaceholder'), unit: "pcs" },
    { value: "productionQuantity", label: t('components.UpdateSummaryInputPopover.options.indicator.productionQuantity'), unit: "pcs" },
    { value: "salesQuantity", label: t('components.UpdateSummaryInputPopover.options.indicator.salesQuantity'), unit: "pcs" },
    { value: "custom", label: t('components.UpdateSummaryInputPopover.options.indicator.custom'), unit: "currency" },
  ], [t]);

  /** 費用グループの項目選択肢（翻訳付き） */
  const COST_ITEM_OPTIONS = useMemo((): ItemOption[] => [
    { value: "", label: t('components.UpdateSummaryInputPopover.options.cost.selectPlaceholder'), unit: "pcs" },
    { value: "laborCost", label: t('components.UpdateSummaryInputPopover.options.cost.laborCost'), unit: "currency" },
    { value: "repairCost", label: t('components.UpdateSummaryInputPopover.options.cost.repairCost'), unit: "currency" },
    { value: "consumablesCost", label: t('components.UpdateSummaryInputPopover.options.cost.consumablesCost'), unit: "currency" },
    { value: "maintenanceCost", label: t('components.UpdateSummaryInputPopover.options.cost.maintenanceCost'), unit: "currency" },
    { value: "scrapCost", label: t('components.UpdateSummaryInputPopover.options.cost.scrapCost'), unit: "currency" },
    { value: "targetEquipmentDepreciation", label: t('components.UpdateSummaryInputPopover.options.cost.targetEquipmentDepreciation'), unit: "currency" },
    { value: "otherDepreciation", label: t('components.UpdateSummaryInputPopover.options.cost.otherDepreciation'), unit: "currency" },
    { value: "custom", label: t('components.UpdateSummaryInputPopover.options.cost.custom'), unit: "currency" },
  ], [t]);

  /** 全ての項目選択肢（表示名取得用） */
  const ALL_ITEM_OPTIONS = useMemo((): ItemOption[] => [
    ...INDICATOR_ITEM_OPTIONS.filter(opt => opt.value !== ""),
    ...COST_ITEM_OPTIONS.filter(opt => opt.value !== ""),
  ], [INDICATOR_ITEM_OPTIONS, COST_ITEM_OPTIONS]);

  const WIDTH_ITEM_NAME = 240;
  const WIDTH_OPERATE = 65;
  const WIDTH_KBN = 100;
  const WIDTH_VALUE = 150;

  /** 累積列幅を返す */
  const getFixedColumnLeft = (columnPos: number) => {
    let width: number = 0;
    if (1 < columnPos) width += WIDTH_ITEM_NAME;
    if (2 < columnPos) width += WIDTH_OPERATE;
    if (3 < columnPos) width += WIDTH_KBN;
    return width;
  };

  /** 指標グループ項目 */
  const [indicatorItems, setIndicatorItems] = useState<RationalizationProfitLossItem[]>(() => ensureRequiredIndicatorItems([]));
  /** 指標グループ項目選択値 */
  const [selectedIndicatorItemType, setSelectedIndicatorItemType] = useState<RationalizationProfitLossItemType | "">("");
  /** 費用グループ項目 */
  const [costItems, setCostItems] = useState<RationalizationProfitLossItem[]>(() => ensureRequiredCostItems([]));
  /** 費用グループ項目選択値 */
  const [selectedCostItemType, setSelectedCostItemType] = useState<RationalizationProfitLossItemType | "">("");
  /** 費用グループ選択通貨 */
  const [selectedCostCurrency, setSelectedCostCurrency] = useState<string>(localCurrency || "");
  const prevCostCurrencyRef = useRef<string>(localCurrency || "");
  /** 設備データキャッシュ */
  const equipmentCacheRef = useRef<EquipmentType[] | null>(null);
  /** 自由入力モーダルの開閉状態 */
  const [isCustomItemModalOpen, setIsCustomItemModalOpen] = useState(false);
  /** 自由入力モーダル：対象グループ */
  const [customItemTargetGroup, setCustomItemTargetGroup] = useState<"indicator" | "cost">("indicator");
  /** 自由入力モーダル：項目名 */
  const [customItemName, setCustomItemName] = useState("");
  /** 自由入力モーダル：単位 */
  const [customUnit, setCustomUnit] = useState("");
  /** 自由入力モーダル：編集対象ID */
  const [editingCustomItemId, setEditingCustomItemId] = useState<string | null>(null);
  /** GoogleSpread処理中フラグ */
  const [isGsLoading, setIsGsLoading] = useState(false);
  /** アクティブなスプレッドシートID（指標グループ） */
  const [activeIndicatorSpreadsheetId, setActiveIndicatorSpreadsheetId] = useState<string | null>(null);
  /** アクティブなスプレッドシートID（費用グループ） */
  const [activeCostSpreadsheetId, setActiveCostSpreadsheetId] = useState<string | null>(null);
  /** スプレッドシートウィンドウが閉じられたか（指標グループ） */
  const [isIndicatorSpreadsheetWindowClosed, setIsIndicatorSpreadsheetWindowClosed] = useState(false);
  /** スプレッドシートウィンドウが閉じられたか（費用グループ） */
  const [isCostSpreadsheetWindowClosed, setIsCostSpreadsheetWindowClosed] = useState(false);
  /** スプレッドシートウィンドウ参照（指標グループ） */
  const [indicatorSpreadsheetWindow, setIndicatorSpreadsheetWindow] = useState<Window | null>(null);
  /** スプレッドシートウィンドウ参照（費用グループ） */
  const [costSpreadsheetWindow, setCostSpreadsheetWindow] = useState<Window | null>(null);
  /** バリデーションエラーセル（指標グループ） */
  const [indicatorErrorCells, setIndicatorErrorCells] = useState<Set<string>>(new Set());
  /** バリデーションエラーセル（費用グループ） */
  const [costErrorCells, setCostErrorCells] = useState<Set<string>>(new Set());
  /** ホバー中の項目ID（指標グループ） */
  const [hoveredIndicatorItemId, setHoveredIndicatorItemId] = useState<string | null>(null);
  /** ホバー中の項目ID（費用グループ） */
  const [hoveredCostItemId, setHoveredCostItemId] = useState<string | null>(null);

  /** 現在選択中のPartNoのデータ（アクセス用ヘルパー） */
  const currentPartNoData = useMemo(() => {
    const selectedPartNo = localUpdateSummary.selectedPartNo;
    if (!selectedPartNo) {
      return {
        recentActual: { ...defaultProductionCapacityData },
        theoretical: { ...defaultProductionCapacityData },
        investmentPlan: { ...defaultProductionCapacityData },
      };
    }
    return localUpdateSummary.productionCapacityByPartNo?.[selectedPartNo] || {
      recentActual: { ...defaultProductionCapacityData },
      theoretical: { ...defaultProductionCapacityData },
      investmentPlan: { ...defaultProductionCapacityData },
    };
  }, [localUpdateSummary.selectedPartNo, localUpdateSummary.productionCapacityByPartNo]);

  /** トースト表示用 */
  const toast = useToast();
  /** Popoverの開閉状態 */
  const [isOpen, setIsOpen] = useState(false);
  /** 生成AI利用制限フラグ（案件の申請部署ベース） */
  const isAIRestricted = useAppSelector((state) => state.employee.isAIRestricted);
  /** システム全体AI利用可否フラグ（GCP Gemini利用グループへの所属） */
  const isAIEnabled = useAppSelector((state) => state.employee.isAIEnabled);
  /** AI分析モーダル表示状態 */
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  /** AI分析実行済みフラグ（モーダルを閉じたらtrue） */
  const [isAIAnalyzed, setIsAIAnalyzed] = useState(false);
  /** AIアドバイス確認済みチェック */
  const [isAIAdviceConfirmed, setIsAIAdviceConfirmed] = useState(false);
  /** 製造ナレッジ検索モーダル表示状態 */
  const [isKnowledgeModalOpen, setIsKnowledgeModalOpen] = useState(false);
  /** 製造ナレッジ検索実行済みフラグ */
  const [isAIKnowledgeAnalyzed, setIsAIKnowledgeAnalyzed] = useState(false);
  /** AI分析用プロンプト（メモ化） */
  const aiPrompt = useMemo(() => {
    const filtered = (equipmentData || []).filter(e => e.processId === productionLineId);
    return generateUpdateSummaryPrompt({
      processName: productionLineName || "",
      equipmentNames: filtered.filter(e => e.equipmentName).map(e => e.equipmentName!),
      equipmentGroupNames: filtered.filter(e => e.equipmentGroupName).map(e => getMultilingualDisplayName(e.equipmentGroupName)),
      depreciationPeriods: filtered.filter(e => e.depreciationPeriod).map(e => e.depreciationPeriod!),
    });
  }, [equipmentData, productionLineId, productionLineName]);
  /** 製造ナレッジ検索用プロンプト（メモ化） */
  const knowledgePrompt = useMemo(() => {
    const filtered = (equipmentData || []).filter(e => e.processId === productionLineId);
    return generateManufacturingKnowledgePrompt({
      productName: projectWithRdsData?.mmProductNameFreeWrite || undefined,
      customerProductName: projectWithRdsData?.customerProductName || undefined,
      customerName: projectWithRdsData?.customerName || undefined,
      customerIndustryHierarchy: projectWithRdsData?.customerIndustryHierarchy || undefined,
      applicationCategory: projectWithRdsData?.applicationCategory || undefined,
      carModel: projectWithRdsData?.carModel || undefined,
      partNo: partNoStrings?.length ? partNoStrings.join(", ") : undefined,
      purposeOfInvestment: projectWithRdsData?.purpose || undefined,
      processName: productionLineName || "",
      equipmentNames: filtered.filter(e => e.equipmentName).map(e => e.equipmentName!),
      equipmentGroupNames: filtered.filter(e => e.equipmentGroupName).map(e => getMultilingualDisplayName(e.equipmentGroupName)),
    });
  }, [equipmentData, productionLineId, productionLineName, projectWithRdsData, partNoStrings]);
  /**保存ボタン活性制御 */
  const isSaving = isSave;

  /** Popoverを開いた時の設備データキャッシュクリア＋償却費自動計算 */
  useEffect(() => {
    if (!isOpen) {
      // 指標・費用グループのSpreadSheetウィンドウを閉じる
      if (indicatorSpreadsheetWindow && !indicatorSpreadsheetWindow.closed) indicatorSpreadsheetWindow.close();
      if (costSpreadsheetWindow && !costSpreadsheetWindow.closed) costSpreadsheetWindow.close();
      return;
    }
    if (!isSimpleMode) return;
    equipmentCacheRef.current = null;
    prevCostCurrencyRef.current = selectedCostCurrency;
    const targetItem = costItems.find(item => item.itemType === "targetEquipmentDepreciation");
    const hasExistingData = targetItem && Object.keys(targetItem.beforeMonthlyData).length > 0;
    if (!hasExistingData) {
      calcTargetEquipmentDepreciation().catch(err => console.error("償却費自動計算エラー:", err));
    } else {
      calcTargetEquipmentDepreciation({ updateBefore: false, updateAfter: false }).catch(err => console.error("償却費キャッシュ取得エラー:", err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /** 入力フィールドの文字列値（フォーマット制御用） */
  const [inputValues, setInputValues] = useState<{ [key: string]: string }>({
    // 基本情報
    "operatingDays": "0",
    "partsCount": "1",
    "cumulativeCost": "0",
    // 生産能力
    "recentActual.yield": "0.00",
    "theoretical.yield": "0.00",
    "investmentPlan.yield": "0.00",
    "recentActual.nonWorkingTime": "0.00",
    "theoretical.nonWorkingTime": "0.00",
    "investmentPlan.nonWorkingTime": "0.00",
    "recentActual.machineTrouble": "0.00",
    "theoretical.machineTrouble": "0.00",
    "investmentPlan.machineTrouble": "0.00",
    "recentActual.maintenance": "0.00",
    "theoretical.maintenance": "0.00",
    "investmentPlan.maintenance": "0.00",
    "recentActual.cycleTime": "0.0",
    "theoretical.cycleTime": "0.0",
    "investmentPlan.cycleTime": "0.0",
    "recentActual.personnelPerDay": "0.0",
    "theoretical.personnelPerDay": "0.0",
    "investmentPlan.personnelPerDay": "0.0",
    // 損益
    "profitLoss.productionQuantity": "0",
    "profitLoss.recentActual.repairCost": "0",
    "profitLoss.theoretical.repairCost": "0",
    "profitLoss.investmentPlan.repairCost": "0",
    "profitLoss.recentActual.consumablesCost": "0",
    "profitLoss.theoretical.consumablesCost": "0",
    "profitLoss.investmentPlan.consumablesCost": "0",
  });

  /** デフォルト値 */
  const DEFAULT_TIME = "00:00";


  /** エラー情報（デフォルト値） */
  const defaultErrors = {
    // 基本情報
    // 固定資産番号
    fixedAssetNumber: {
      isError: false,
      errorMessage: "",
    },
    // 稼働日数
    operatingDays: {
      isError: false,
      errorMessage: "",
    },
    // 労働時間
    workTime: {
      isError: false,
      errorMessage: "",
    },
    // 勤務体制
    shiftSystem: {
      isError: false,
      errorMessage: "",
    },
    // 部品数
    partsCount: {
      isError: false,
      errorMessage: "",
    },
    // 当該工程までの累積原価
    cumulativeCost: {
      isError: false,
      errorMessage: "",
    },
    // 生産能力（PartNo選択エラー）
    productionCapacity: {
      isError: false,
      errorMessage: "",
    },
    // 分析
    // 更新をする理由
    updateReason: {
      isError: false,
      errorMessage: "",
    },
    // 設備あたり生産性
    productionPerEquipment: {
      isError: false,
      errorMessage: "",
    },
    // 1人あたり生産性
    productionPerPerson: {
      isError: false,
      errorMessage: "",
    },
    // 非稼働対策
    nonOperationMeasures: {
      isError: false,
      errorMessage: "",
    },
    // 非加工対策
    nonProcessingMeasures: {
      isError: false,
      errorMessage: "",
    },
    // 現行設備の処置方法
    currentEquipmentHandling: {
      isError: false,
      errorMessage: "",
    },
    // 損益
    // 生産数
    productionQuantity: {
      isError: false,
      errorMessage: "",
    },
    // 費用グループ
    costCurrency: {
      isError: false,
      errorMessage: "",
    },
  };

  /** PartNoごとの生産能力エラー情報の型 */
  type ProductionCapacityErrors = {
    // 歩留
    recentActualYield: { isError: boolean; errorMessage: string };
    theoreticalYield: { isError: boolean; errorMessage: string };
    investmentPlanYield: { isError: boolean; errorMessage: string };
    // サイクルタイム
    recentActualCycleTime: { isError: boolean; errorMessage: string };
    theoreticalCycleTime: { isError: boolean; errorMessage: string };
    investmentPlanCycleTime: { isError: boolean; errorMessage: string };
    // 1日あたり必要人員数
    recentActualPersonnelPerDay: { isError: boolean; errorMessage: string };
    theoreticalPersonnelPerDay: { isError: boolean; errorMessage: string };
    investmentPlanPersonnelPerDay: { isError: boolean; errorMessage: string };
  };

  /** デフォルトのPartNoエラー */
  const defaultPartNoErrors: ProductionCapacityErrors = {
    // 歩留
    recentActualYield: { isError: false, errorMessage: "" },
    theoreticalYield: { isError: false, errorMessage: "" },
    investmentPlanYield: { isError: false, errorMessage: "" },
    // サイクルタイム
    recentActualCycleTime: { isError: false, errorMessage: "" },
    theoreticalCycleTime: { isError: false, errorMessage: "" },
    investmentPlanCycleTime: { isError: false, errorMessage: "" },
    // 1日あたり必要人員数
    recentActualPersonnelPerDay: { isError: false, errorMessage: "" },
    theoreticalPersonnelPerDay: { isError: false, errorMessage: "" },
    investmentPlanPersonnelPerDay: { isError: false, errorMessage: "" },
  };

  /** エラー情報 */
  const [errors, setErrors] = useState<Errors>(JSON.parse(JSON.stringify(defaultErrors)));

  /** PartNoごとのエラー情報 */
  const [errorsByPartNo, setErrorsByPartNo] = useState<{ [partNo: string]: ProductionCapacityErrors }>({});


  /**
   * 初期化: 既存データがあればセット
   */
  useEffect(() => {
    if (updateSummaryData) {
      // 初期データをセットし、全PartNoの計算値を算出
      let initialData = { ...updateSummaryData };
      const originalSelectedPartNo = updateSummaryData.selectedPartNo;

      // 全PartNoの計算値を算出
      const allPartNos = Object.keys(updateSummaryData.productionCapacityByPartNo || {});
      allPartNos.forEach((partNo) => {
        initialData = { ...initialData, selectedPartNo: partNo };
        ["recentActual", "theoretical", "investmentPlan"].forEach((stateType) => {
          initialData = calculateUpdateSummary(initialData, stateType as any);
        });
      });

      // selectedPartNoを元に戻す
      initialData = { ...initialData, selectedPartNo: originalSelectedPartNo };
      setLocalUpdateSummary(initialData);

      // 指標グループと費用グループの初期化
      setIndicatorItems(ensureRequiredIndicatorItems(initialData.indicatorItems || []));
      setCostItems(ensureRequiredCostItems(initialData.costItems || []));
      if (initialData.costCurrencyId) {
        setSelectedCostCurrency(initialData.costCurrencyId);
      }

      // 選択中のPartNoのデータを取得
      const selectedPartNo = initialData.selectedPartNo;
      const partNoData = selectedPartNo
        ? initialData.productionCapacityByPartNo?.[selectedPartNo]
        : null;

      // inputValuesも初期化
      setInputValues({
        // 基本情報
        "operatingDays": String(initialData.operatingDays ?? 0),
        "partsCount": String(initialData.partsCount ?? 1),
        "cumulativeCost": String(initialData.cumulativeCost ?? 0),
        // 生産能力
        "recentActual.yield": (partNoData?.recentActual?.yield ?? 0).toFixed(2),
        "theoretical.yield": (partNoData?.theoretical?.yield ?? 0).toFixed(2),
        "investmentPlan.yield": (partNoData?.investmentPlan?.yield ?? 0).toFixed(2),
        "recentActual.nonWorkingTime": (partNoData?.recentActual?.nonWorkingTime ?? 0).toFixed(2),
        "theoretical.nonWorkingTime": (partNoData?.theoretical?.nonWorkingTime ?? 0).toFixed(2),
        "investmentPlan.nonWorkingTime": (partNoData?.investmentPlan?.nonWorkingTime ?? 0).toFixed(2),
        "recentActual.machineTrouble": (partNoData?.recentActual?.machineTrouble ?? 0).toFixed(2),
        "theoretical.machineTrouble": (partNoData?.theoretical?.machineTrouble ?? 0).toFixed(2),
        "investmentPlan.machineTrouble": (partNoData?.investmentPlan?.machineTrouble ?? 0).toFixed(2),
        "recentActual.maintenance": (partNoData?.recentActual?.maintenance ?? 0).toFixed(2),
        "theoretical.maintenance": (partNoData?.theoretical?.maintenance ?? 0).toFixed(2),
        "investmentPlan.maintenance": (partNoData?.investmentPlan?.maintenance ?? 0).toFixed(2),
        "recentActual.cycleTime": (partNoData?.recentActual?.cycleTime ?? 0).toFixed(1),
        "theoretical.cycleTime": (partNoData?.theoretical?.cycleTime ?? 0).toFixed(1),
        "investmentPlan.cycleTime": (partNoData?.investmentPlan?.cycleTime ?? 0).toFixed(1),
        "recentActual.personnelPerDay": (partNoData?.recentActual?.personnelPerDay ?? 0).toFixed(1),
        "theoretical.personnelPerDay": (partNoData?.theoretical?.personnelPerDay ?? 0).toFixed(1),
        "investmentPlan.personnelPerDay": (partNoData?.investmentPlan?.personnelPerDay ?? 0).toFixed(1),
        // 損益
        "profitLoss.productionQuantity": String(initialData.profitLoss?.productionQuantity ?? 0),
        "profitLoss.recentActual.repairCost": String(initialData.profitLoss?.recentActual?.repairCost ?? 0),
        "profitLoss.theoretical.repairCost": String(initialData.profitLoss?.theoretical?.repairCost ?? 0),
        "profitLoss.investmentPlan.repairCost": String(initialData.profitLoss?.investmentPlan?.repairCost ?? 0),
        "profitLoss.recentActual.consumablesCost": String(initialData.profitLoss?.recentActual?.consumablesCost ?? 0),
        "profitLoss.theoretical.consumablesCost": String(initialData.profitLoss?.theoretical?.consumablesCost ?? 0),
        "profitLoss.investmentPlan.consumablesCost": String(initialData.profitLoss?.investmentPlan?.consumablesCost ?? 0),
      });
    }
  }, [updateSummaryData]);

  /**
   * フィールド更新ハンドラー
   */
  const handleUpdate = (field: string, value: any) => {
    setLocalUpdateSummary((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * 最大稼働日数（1ヶ月の最大日数）
   */
  const maxOperatingDays = 31;

  /**
   * 状態別データ更新ハンドラー（productionCapacityByPartNoのみ更新）
   */
  const handleStateUpdate = (
    stateType: "recentActual" | "theoretical" | "investmentPlan",
    field: keyof ProductionCapacityData,
    value: any
  ) => {
    setLocalUpdateSummary((prev) => {
      const selectedPartNo = prev.selectedPartNo;
      if (!selectedPartNo) {
        return prev;
      }

      const currentData = prev.productionCapacityByPartNo?.[selectedPartNo] || {
        recentActual: { ...defaultProductionCapacityData },
        theoretical: { ...defaultProductionCapacityData },
        investmentPlan: { ...defaultProductionCapacityData },
      };

      const updatedState = {
        ...currentData[stateType],
        [field]: value,
      };

      const updated = {
        ...prev,
        productionCapacityByPartNo: {
          ...prev.productionCapacityByPartNo,
          [selectedPartNo]: {
            ...currentData,
            [stateType]: updatedState,
          },
        },
      };

      return calculateUpdateSummary(updated, stateType);
    });
  };

  /**
   * 入力値変更ハンドラー（文字列をそのまま保持）
   */
  const handleInputChange = (
    stateType: "recentActual" | "theoretical" | "investmentPlan",
    field: keyof ProductionCapacityData,
    value: string
  ) => {
    const key = `${stateType}.${field}`;
    setInputValues(prev => ({ ...prev, [key]: value }));
    // localUpdateSummaryも更新（計算用）
    handleStateUpdate(stateType, field, Number(value) || 0);
  };

  /**
   * フォーカスが外れた時にフォーマット
   */
  const handleInputBlur = (
    stateType: "recentActual" | "theoretical" | "investmentPlan",
    field: keyof ProductionCapacityData
  ) => {
    const key = `${stateType}.${field}`;
    const currentValue = inputValues[key];
    // 数値に変換
    const parsedValue = Number(currentValue);
    // 最大値・最小値の制限を適用（フィールドごとに異なる）
    let max = 100000;
    if (field === "yield") max = 100;
    else if (field === "nonWorkingTime" || field === "machineTrouble" || field === "maintenance") max = 24;
    const clampedValue = Math.min(Math.max(parsedValue || 0, 0), max);
    // 小数点以下の桁数（precision=2は.toFixed(2)、precision=1は.toFixed(1)）
    const decimals = (field === "cycleTime" || field === "personnelPerDay") ? 1 : 2;
    const formattedValue = clampedValue.toFixed(decimals);
    // 文字列状態を更新
    setInputValues(prev => ({ ...prev, [key]: formattedValue }));
    // localUpdateSummaryも更新
    handleStateUpdate(stateType, field, clampedValue);
  };

  /**
   * 損益セクション入力値変更ハンドラー（文字列をそのまま保持）
   */
  const handleProfitLossInputChange = (
    stateType: "recentActual" | "theoretical" | "investmentPlan",
    field: "repairCost" | "consumablesCost",
    value: string
  ) => {
    const key = `profitLoss.${stateType}.${field}`;
    setInputValues(prev => ({ ...prev, [key]: value }));
    // localUpdateSummaryも更新（計算用）
    const numericValue = value === "" ? 0 : Number(value);
    setLocalUpdateSummary(prev => ({
      ...prev,
      profitLoss: {
        ...prev.profitLoss,
        [stateType]: {
          ...prev.profitLoss?.[stateType],
          [field]: isNaN(numericValue) ? 0 : numericValue
        }
      }
    }));
  };

  /**
   * 損益セクションフォーカスが外れた時にフォーマット
   */
  const handleProfitLossInputBlur = (
    stateType: "recentActual" | "theoretical" | "investmentPlan",
    field: "repairCost" | "consumablesCost"
  ) => {
    const key = `profitLoss.${stateType}.${field}`;
    const currentValue = inputValues[key];
    // 数値に変換
    const parsedValue = Number(currentValue);
    // 最大値・最小値の制限を適用
    const clampedValue = Math.max(parsedValue || 0, 0);
    // 整数で表示
    const formattedValue = String(Math.round(clampedValue));
    // 文字列状態を更新
    setInputValues(prev => ({ ...prev, [key]: formattedValue }));
    // localUpdateSummaryも更新
    setLocalUpdateSummary(prev => ({
      ...prev,
      profitLoss: {
        ...prev.profitLoss,
        [stateType]: {
          ...prev.profitLoss?.[stateType],
          [field]: clampedValue
        }
      }
    }));
  };

  /**
   * 生産数入力値変更ハンドラー（文字列をそのまま保持）
   */
  const handleProductionQuantityInputChange = (value: string) => {
    const key = "profitLoss.productionQuantity";
    setInputValues(prev => ({ ...prev, [key]: value }));
    const numericValue = value === "" ? 0 : Number(value);
    setLocalUpdateSummary(prev => ({
      ...prev,
      profitLoss: {
        ...prev.profitLoss,
        productionQuantity: isNaN(numericValue) ? 0 : numericValue
      }
    }));
  };

  /**
   * 生産数フォーカスが外れた時にフォーマット
   */
  const handleProductionQuantityInputBlur = () => {
    const key = "profitLoss.productionQuantity";
    const currentValue = inputValues[key];
    const parsedValue = Number(currentValue);
    const clampedValue = Math.max(parsedValue || 0, 0);
    const formattedValue = String(Math.round(clampedValue));
    setInputValues(prev => ({ ...prev, [key]: formattedValue }));
    setLocalUpdateSummary(prev => ({
      ...prev,
      profitLoss: {
        ...prev.profitLoss,
        productionQuantity: clampedValue
      }
    }));
  };

  /**
   * 稼働日数入力値変更ハンドラー（文字列をそのまま保持）
   */
  const handleOperatingDaysInputChange = (value: string) => {
    setInputValues(prev => ({ ...prev, "operatingDays": value }));
    const numericValue = value === "" ? 0 : Number(value);
    const safeValue = isNaN(numericValue) ? 0 : numericValue;
    handleUpdate("operatingDays", safeValue);
    // 稼働日数変更時に各状態を再計算
    ["recentActual", "theoretical", "investmentPlan"].forEach((stateType) => {
      setLocalUpdateSummary((prev) =>
        calculateUpdateSummary({ ...prev, operatingDays: safeValue }, stateType as any)
      );
    });
  };

  /**
   * 稼働日数フォーカスが外れた時にフォーマット
   */
  const handleOperatingDaysInputBlur = () => {
    const currentValue = inputValues["operatingDays"];
    const parsedValue = Number(currentValue);
    const clampedValue = Math.min(Math.max(parsedValue || 0, 0), maxOperatingDays);
    const formattedValue = String(Math.round(clampedValue));
    setInputValues(prev => ({ ...prev, "operatingDays": formattedValue }));
    handleUpdate("operatingDays", clampedValue);
    // 稼働日数変更時に各状態を再計算
    ["recentActual", "theoretical", "investmentPlan"].forEach((stateType) => {
      setLocalUpdateSummary((prev) =>
        calculateUpdateSummary({ ...prev, operatingDays: clampedValue }, stateType as any)
      );
    });
  };

  /**
   * 部品数入力値変更ハンドラー（文字列をそのまま保持）
   */
  const handlePartsCountInputChange = (value: string) => {
    setInputValues(prev => ({ ...prev, "partsCount": value }));
    const numericValue = value === "" ? 1 : Number(value);
    const safeValue = isNaN(numericValue) ? 1 : Math.max(numericValue, 1);
    handleUpdate("partsCount", safeValue);
  };

  /**
   * 部品数フォーカスが外れた時にフォーマット
   */
  const handlePartsCountInputBlur = () => {
    const currentValue = inputValues["partsCount"];
    const parsedValue = Number(currentValue);
    const clampedValue = Math.max(parsedValue || 1, 1);
    const formattedValue = String(Math.round(clampedValue));
    setInputValues(prev => ({ ...prev, "partsCount": formattedValue }));
    handleUpdate("partsCount", clampedValue);
  };

  /**
   * 累積原価入力値変更ハンドラー（文字列をそのまま保持）
   */
  const handleCumulativeCostInputChange = (value: string) => {
    setInputValues(prev => ({ ...prev, "cumulativeCost": value }));
    const numericValue = value === "" ? 0 : Number(value);
    const safeValue = isNaN(numericValue) ? 0 : numericValue;
    handleUpdate("cumulativeCost", safeValue);
  };

  /**
   * 累積原価フォーカスが外れた時にフォーマット
   */
  const handleCumulativeCostInputBlur = () => {
    const currentValue = inputValues["cumulativeCost"];
    const parsedValue = Number(currentValue);
    const clampedValue = Math.max(parsedValue || 0, 0);
    const formattedValue = String(Math.round(clampedValue));
    setInputValues(prev => ({ ...prev, "cumulativeCost": formattedValue }));
    handleUpdate("cumulativeCost", clampedValue);
  };

  /**
   * テキストフィールド更新ハンドラー
   */
  const handleTextFieldUpdate = (field: keyof UpdateSummaryInputData, value: string) => {
    setLocalUpdateSummary((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * 更新前後サマリー計算
   */
  const calculateUpdateSummary = (
    summaryData: UpdateSummaryInputData,
    stateType: "recentActual" | "theoretical" | "investmentPlan"
  ): UpdateSummaryInputData => {
    const selectedPartNo = summaryData.selectedPartNo;
    if (!selectedPartNo) return summaryData;

    const partNoData = summaryData.productionCapacityByPartNo?.[selectedPartNo];
    if (!partNoData) return summaryData;

    const state = partNoData[stateType];
    if (!state) return summaryData;

    const { yield: yieldRate, nonWorkingTime, cycleTime, personnelPerDay } = state;
    const { operatingDays } = summaryData;
    const workTime = hourMinutesStringToMinutes(summaryData.workTime) / 60;
    const shiftSystem = Number(summaryData.shiftSystem);

    // 稼働率 = (24 - 非稼働時間) / 24 × 100
    const operatingRate = ((24 - (nonWorkingTime ?? 0)) / 24) * 100;

    // 生産能力 = (稼働日数 × 24 × 3600) / サイクルタイム × 稼働率 × 歩留
    let productionVolume = 0;
    if (cycleTime && cycleTime > 0 && yieldRate && operatingDays) {
      productionVolume = Math.round(
        ((operatingDays * 24 * 3600) / cycleTime) *
        (operatingRate / 100) *
        (yieldRate / 100)
      );
    }

    // 設備あたり生産性 = 生産能力 / 稼働日数 / 24
    let productionPerEquipment = 0;
    if (productionVolume > 0 && operatingDays && operatingDays > 0) {
      productionPerEquipment = Math.round((productionVolume / operatingDays / 24) * 100) / 100;
    }

    // 1人あたり生産性 = （生産能力 / 稼働日数 / （シフト数 × 労働時間））/ シフト数 × 人数
    let productionPerPerson = 0;
    if (productionVolume > 0 && operatingDays && operatingDays > 0 && shiftSystem > 0 && workTime > 0 && (personnelPerDay ?? 0) > 0) {
      productionPerPerson = Math.round(
        ((productionVolume / operatingDays / (shiftSystem * workTime)) /
          (shiftSystem * (personnelPerDay ?? 0))) * 100
      ) / 100;
    }

    return {
      ...summaryData,
      productionCapacityByPartNo: {
        ...summaryData.productionCapacityByPartNo,
        [selectedPartNo]: {
          ...partNoData,
          [stateType]: {
            ...state,
            operatingRate,
            productionVolume,
            productionPerEquipment,
            productionPerPerson,
          },
        },
      },
    };
  };

  /** グループ種別 */
  type GroupType = "indicator" | "cost";

  /** グループに応じたstate操作を取得するヘルパー */
  const getGroupStateHandlers = (group: GroupType) => ({
    items: group === "indicator" ? indicatorItems : costItems,
    setItems: group === "indicator" ? setIndicatorItems : setCostItems,
    setSelectedItemType: group === "indicator" ? setSelectedIndicatorItemType : setSelectedCostItemType,
    errorCells: group === "indicator" ? indicatorErrorCells : costErrorCells,
    setErrorCells: group === "indicator" ? setIndicatorErrorCells : setCostErrorCells,
    activeSpreadsheetId: group === "indicator" ? activeIndicatorSpreadsheetId : activeCostSpreadsheetId,
    setActiveSpreadsheetId: group === "indicator" ? setActiveIndicatorSpreadsheetId : setActiveCostSpreadsheetId,
    spreadsheetWindow: group === "indicator" ? indicatorSpreadsheetWindow : costSpreadsheetWindow,
    setSpreadsheetWindow: group === "indicator" ? setIndicatorSpreadsheetWindow : setCostSpreadsheetWindow,
    isSpreadsheetWindowClosed: group === "indicator" ? isIndicatorSpreadsheetWindowClosed : isCostSpreadsheetWindowClosed,
    setIsSpreadsheetWindowClosed: group === "indicator" ? setIsIndicatorSpreadsheetWindowClosed : setIsCostSpreadsheetWindowClosed,
    itemOptions: group === "indicator" ? INDICATOR_ITEM_OPTIONS : COST_ITEM_OPTIONS,
  });

  /** 項目を作成するヘルパー */
  const createItem = (
    itemType: RationalizationProfitLossItemType,
    customItemNameValue?: string,
    customUnitValue?: string
  ): RationalizationProfitLossItem => {
    const option = ALL_ITEM_OPTIONS.find(opt => opt.value === itemType);
    const newItem: RationalizationProfitLossItem = {
      id: uuidv4(),
      itemType,
      customItemName: customItemNameValue,
      unitType: itemType === "custom" ? "custom" : (option?.unit ?? "currency"),
      customUnit: customUnitValue,
      beforeMonthlyData: {},
      afterMonthlyData: {},
    };
    monthList.forEach(month => {
      newItem.beforeMonthlyData[month] = 0;
      newItem.afterMonthlyData[month] = 0;
    });
    return newItem;
  };

  /** 項目を追加 */
  const addItem = (
    group: GroupType,
    itemType: RationalizationProfitLossItemType,
    customItemNameValue?: string,
    customUnitValue?: string
  ) => {
    const newItem = createItem(itemType, customItemNameValue, customUnitValue);
    const { setItems, setSelectedItemType } = getGroupStateHandlers(group);
    setItems(prev => [...prev, newItem]);
    setSelectedItemType("");
  };

  /** 項目を削除 */
  const removeItem = (group: GroupType, itemId: string) => {
    const { setItems } = getGroupStateHandlers(group);
    setItems(prev => prev.filter(item => item.id !== itemId));
  };

  /** 項目を上下に並び替える */
  const moveItem = (group: GroupType, itemId: string, direction: "up" | "down") => {
    const { setItems } = getGroupStateHandlers(group);
    setItems(prev => {
      const index = prev.findIndex(item => item.id === itemId);
      if (index < 0) return prev;
      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  };

  /** 項目の表示名を取得 */
  const getItemDisplayName = (item: RationalizationProfitLossItem): string => {
    if (item.itemType === "custom") {
      return item.customItemName || t('components.UpdateSummaryInputPopover.options.indicator.custom');
    }
    const option = ALL_ITEM_OPTIONS.find(opt => opt.value === item.itemType);
    return option?.label || "";
  };

  /** 項目の単位を取得 */
  const getItemUnit = (item: RationalizationProfitLossItem, group: "indicator" | "cost"): string => {
    if (group === "cost") {
      const currency = currencyData.find(c => c.currencyId === selectedCostCurrency);
      return currency?.currencyName || "";
    }
    if (item.itemType === "custom") return item.customUnit || "";
    const option = ALL_ITEM_OPTIONS.find(opt => opt.value === item.itemType);
    return option?.unit || "";
  };

  /** 項目選択変更ハンドラー */
  const handleItemTypeChange = (group: GroupType, value: RationalizationProfitLossItemType | "") => {
    if (!value) return;
    const { setSelectedItemType } = getGroupStateHandlers(group);
    if (value === "custom") {
      setSelectedItemType(value);
      setCustomItemTargetGroup(group);
      if (group === "cost") setCustomUnit("通貨");
      setIsCustomItemModalOpen(true);
    } else {
      addItem(group, value);
      setSelectedItemType("");
    }
  };

  /** 自由入力モーダルの確定ハンドラー */
  const handleCustomItemConfirm = () => {
    if (customItemName.trim() && customUnit.trim()) {
      const { setItems, setErrorCells } = getGroupStateHandlers(customItemTargetGroup);
      if (editingCustomItemId) {
        setItems(prev =>
          prev.map(item =>
            item.id === editingCustomItemId
              ? { ...item, customItemName: customItemName.trim(), customUnit: customUnit.trim() }
              : item
          )
        );
        setErrorCells(prev => {
          const newSet = new Set(prev);
          newSet.delete(`${editingCustomItemId}:item`);
          return newSet;
        });
      } else {
        addItem(customItemTargetGroup, "custom", customItemName.trim(), customUnit.trim());
      }
      setCustomItemName("");
      setCustomUnit("");
      setIsCustomItemModalOpen(false);
      setEditingCustomItemId(null);
    }
  };

  /** 自由入力モーダルのキャンセルハンドラー */
  const handleCustomItemCancel = () => {
    setCustomItemName("");
    setCustomUnit("");
    setIsCustomItemModalOpen(false);
    setEditingCustomItemId(null);
    const { setSelectedItemType } = getGroupStateHandlers(customItemTargetGroup);
    setSelectedItemType("");
  };

  /** 自由入力項目の編集開始ハンドラー */
  const handleEditCustomItem = (group: GroupType, item: RationalizationProfitLossItem) => {
    if (item.itemType !== "custom") return;
    setEditingCustomItemId(item.id);
    setCustomItemName(item.customItemName || "");
    setCustomUnit(item.customUnit || (group === "cost" ? "通貨" : ""));
    setCustomItemTargetGroup(group);
    setIsCustomItemModalOpen(true);
  };

  /**
   * 工程の設備データを取得し targetEquipmentDepreciation の月別償却費を自動計算する
   */
  const calcTargetEquipmentDepreciation = async (options?: { updateBefore?: boolean; updateAfter?: boolean; targetCurrency?: string }) => {
    const { updateBefore = true, updateAfter = true, targetCurrency } = options ?? {};
    if (!productionLineId || !projectStartDate || monthList.length === 0) return;

    let allEquipments: EquipmentType[];
    if (equipmentCacheRef.current) {
      allEquipments = equipmentCacheRef.current;
    } else {
      const response = await WebAPI.request({
        query: `
          query getEquipmentByProcessId($projectId: String, $processId: String) {
            getEquipmentByProcessId(projectId: $projectId, processId: $processId) {
              projectId
              amount
              unitPrice
              currencyId
              depreciationPeriod
              depreciationStartMonth
              acceptanceMonth
            }
          }`,
        variables: { projectId, processId: productionLineId }
      });
      if (!response?.data?.getEquipmentByProcessId) return;
      allEquipments = (response.data.getEquipmentByProcessId as EquipmentType[]).map(
        (e: EquipmentType) => ({ ...e, isReadonly: e.projectId !== projectId })
      );
      equipmentCacheRef.current = allEquipments;
    }

    calcDepreciationFromCache(targetCurrency ?? selectedCostCurrency, allEquipments, { updateBefore, updateAfter });
  };

  /** キャッシュ済みの設備データから月別償却費を同期計算し setCostItems を呼ぶ */
  const calcDepreciationFromCache = (
    targetCurrencyId: string,
    equipments: EquipmentType[],
    options: { updateBefore: boolean; updateAfter: boolean }
  ) => {
    if (!projectStartDate || monthList.length === 0) return;
    const { updateBefore, updateAfter } = options;
    const currency = currencyData.find(c => c.currencyId === targetCurrencyId);
    const targetCurrencyName = currency?.currencyName ?? targetCurrencyId;
    const maxYears = Math.ceil(monthList.length / 12) + 1;

    const toMonthlyRecord = (equips: EquipmentType[]): Record<string, number> => {
      const converted = convertEquipmentData(equips, targetCurrencyName, currencyData);
      const depreciation = calcEquipmentDepreciationByYearMonth(
        maxYears, converted, hasDecimal(targetCurrencyName), projectStartDate, false
      );
      const result: Record<string, number> = {};
      let current = dayjs(projectStartDate);
      let fiscalYear = 1;
      monthList.forEach((monthStr, i) => {
        if (i > 0 && current.month() + 1 === 4) fiscalYear++;
        const calMonth = current.month() + 1;
        const found = depreciation.find(d => d.year === fiscalYear && d.month === calMonth);
        result[monthStr] = found?.value.toNumber() ?? 0;
        current = current.add(1, "month");
      });
      return result;
    };

    const afterMonthlyData = toMonthlyRecord(equipments.filter(e => !e.isReadonly));
    const beforeMonthlyData = toMonthlyRecord(equipments.filter(e => e.isReadonly));

    setCostItems(prev => prev.map(item =>
      item.itemType === "targetEquipmentDepreciation"
        ? {
          ...item,
          ...(updateBefore ? { beforeMonthlyData, isManuallyEditedBefore: false } : {}),
          ...(updateAfter ? { afterMonthlyData, isManuallyEditedAfter: false } : {}),
        }
        : item
    ));
  };

  /** GoogleSpreadボタン押下（グループ共通） */
  const handleGroupGoogleSpread = async (group: GroupType) => {
    const { items } = getGroupStateHandlers(group);
    const groupDisplayName = group === "indicator"
      ? t('components.UpdateSummaryInputPopover.section.indicator')
      : t('components.UpdateSummaryInputPopover.section.cost');
    try {
      setIsGsLoading(true);

      // ポップアップブロック回避：非同期処理より前にユーザー操作起因でウィンドウを確保
      // 既存ウィンドウが生きていれば再利用、なければ新規ブランクタブを開く
      const existingWindow = group === "indicator" ? indicatorSpreadsheetWindow : costSpreadsheetWindow;
      let newWindow: Window | null = (existingWindow && !existingWindow.closed)
        ? existingWindow
        : window.open("", "_blank");

      if (!await googleAPI.signIn()) throw new Error("signIn error");

      const spreadSheetPath = `${SPREAD_SHEET_PATH}/${encodeURIComponent(projectId)}`;
      const parentId = await googleAPI.createFolderPath(spreadSheetPath, null);
      const fileName = `${projectId}-${productionLineId || "unknown"}-${groupDisplayName}`;
      const spreadsheetId = await googleAPI.createSpreadSheet(fileName, parentId!, null);
      const sheetName = groupDisplayName;
      await googleAPI.addSheets(spreadsheetId, [sheetName]);

      const spreadsheetInfo = await googleAPI.getSpreadSheetInfo(spreadsheetId);
      const sheetId = spreadsheetInfo.sheets?.find((s: any) => s.properties?.title === sheetName)?.properties?.sheetId ?? 0;
      // 前回設定した書式・入力規則を初期化（列構成変更時に古い設定が残らないようにする。値の書き込みより前に実行）
      await googleAPI.resetSheetFormatAndValidation(spreadsheetId, [sheetId]);

      await googleAPI.clear(spreadsheetId, sheetName);

      const headerRow = [t('components.UpdateSummaryInputPopover.label.itemName'), t('components.UpdateSummaryInputPopover.label.kbn'), ...monthList.map(m => formatMonthDisplay(m))];
      const itemOptions = group === "indicator"
        ? INDICATOR_ITEM_OPTIONS.filter(opt => opt.value !== "" && opt.value !== "custom").map(opt => opt.label)
        : COST_ITEM_OPTIONS.filter(opt => opt.value !== "" && opt.value !== "custom").map(opt => opt.label);

      const dataRows: string[][] = [];
      items.forEach((item) => {
        const itemName = getItemDisplayName(item);
        dataRows.push([itemName, t('components.UpdateSummaryInputPopover.label.beforeUpdate'), ...monthList.map(m => String(item.beforeMonthlyData[m] ?? 0))]);
        dataRows.push(["", t('components.UpdateSummaryInputPopover.label.afterUpdate'), ...monthList.map(m => String(item.afterMonthlyData[m] ?? 0))]);
      });

      const allRows = [headerRow, ...dataRows];
      await googleAPI.spreadSheetSetValue(spreadsheetId, sheetName, 1, 1, allRows);

      if (items.length > 0) {
        const startRow = 2;
        const endRow = 1 + (items.length * 2);
        await googleAPI.spreadSheetSetDataValidation(spreadsheetId, [{
          sheetId,
          startRow,
          endRow,
          startCol: 1,
          endCol: 1,
          values: itemOptions,
          strict: false,
          showDropdown: true,
        }]);
      }

      if (group === "indicator") {
        setActiveIndicatorSpreadsheetId(spreadsheetId);
        setIndicatorErrorCells(new Set());
      } else {
        setActiveCostSpreadsheetId(spreadsheetId);
        setCostErrorCells(new Set());
      }

      // 事前確保したウィンドウをスプレッドシートに遷移
      const spreadSheetUrl = googleAPI.getOpenSpradSheetUrl(spreadsheetId);
      if (newWindow) {
        newWindow.location.href = spreadSheetUrl;
        newWindow.focus();
        if (group === "indicator") {
          setIndicatorSpreadsheetWindow(newWindow);
          setIsIndicatorSpreadsheetWindowClosed(false);
        } else {
          setCostSpreadsheetWindow(newWindow);
          setIsCostSpreadsheetWindowClosed(false);
        }
        // GoogleSpreadシートウィンドウを手動で閉じられた場合
        const interval = setInterval(() => {
          if (newWindow?.closed) {
            clearInterval(interval); // 監視を停止
            if (group === "indicator") setIsIndicatorSpreadsheetWindowClosed(true);
            else setIsCostSpreadsheetWindowClosed(true);
          }
        }, 500); // 500ms ごとに監視
      }
    } catch (error) {
      console.error("handleGroupGoogleSpread Error.", error);
      toast({
        title: TITLE,
        description: t('components.UpdateSummaryInputPopover.message.googleSpreadUnavailable'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsGsLoading(false);
    }
  };

  /** データ連携ボタン押下（グループ共通） */
  const handleGroupDataLink = async (group: GroupType) => {
    const { activeSpreadsheetId } = getGroupStateHandlers(group);
    if (!activeSpreadsheetId) {
      toast({
        title: TITLE,
        description: t('components.UpdateSummaryInputPopover.message.pressGoogleSpreadFirst'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const groupDisplayName = group === "indicator"
      ? t('components.UpdateSummaryInputPopover.section.indicator')
      : t('components.UpdateSummaryInputPopover.section.cost');
    const beforeLabel = t('components.UpdateSummaryInputPopover.label.beforeUpdate');
    const afterLabel = t('components.UpdateSummaryInputPopover.label.afterUpdate');

    try {
      setIsGsLoading(true);
      if (!await googleAPI.signIn()) throw new Error("signIn error");

      const sheetData = await googleAPI.spreadSheetGetValues(activeSpreadsheetId, groupDisplayName);
      if (!sheetData.values || sheetData.values.length < 2) {
        toast({
          title: TITLE,
          description: t('components.UpdateSummaryInputPopover.message.noSpreadsheetData'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const headerRow = sheetData.values[0] as string[];
      const monthColumnMap: Record<string, number> = {};
      for (let i = 2; i < headerRow.length; i++) {
        const monthHeader = headerRow[i];
        const match = monthHeader?.match(/(\d{4})年(\d{1,2})月/);
        if (match) {
          const yearMonth = `${match[1]}-${match[2].padStart(2, '0')}`;
          monthColumnMap[yearMonth] = i;
        }
      }

      const dataRows = sheetData.values.slice(1);
      const newItems: RationalizationProfitLossItem[] = [];
      const errorCells = new Set<string>();

      const optionsList = group === "indicator" ? INDICATOR_ITEM_OPTIONS : COST_ITEM_OPTIONS;
      const itemLabelToType: Record<string, RationalizationProfitLossItemType> = {};
      optionsList.forEach(opt => {
        if (opt.value !== "") itemLabelToType[opt.label] = opt.value as RationalizationProfitLossItemType;
      });

      let rowIndex = 0;
      while (rowIndex < dataRows.length) {
        const beforeRow = dataRows[rowIndex];
        const afterRow = dataRows[rowIndex + 1];
        if (!beforeRow || beforeRow.length < 2) { rowIndex += 2; continue; }

        const itemName = String(beforeRow[0] || "").trim();
        const beforeCategory = String(beforeRow[1] || "").trim();
        const afterCategory = afterRow ? String(afterRow[1] || "").trim() : "";

        if (!itemName) { rowIndex += 2; continue; }
        if (beforeCategory !== beforeLabel || afterCategory !== afterLabel) { rowIndex += 2; continue; }

        let itemType: RationalizationProfitLossItemType;
        let customItemNameVal: string | undefined;
        let customUnitVal: string | undefined;
        let hasItemError = false;

        if (itemLabelToType[itemName]) {
          itemType = itemLabelToType[itemName];
        } else {
          itemType = "custom";
          customItemNameVal = itemName;
          if (group === "cost") {
            customUnitVal = "通貨";
          } else {
            const existingItem = indicatorItems.find(item => item.itemType === "custom" && item.customItemName === itemName);
            if (existingItem?.customUnit) {
              customUnitVal = existingItem.customUnit;
            } else {
              customUnitVal = "";
              hasItemError = true;
            }
          }
        }

        const newItemId = uuidv4();
        const beforeMonthlyData: Record<string, number> = {};
        const afterMonthlyData: Record<string, number> = {};

        monthList.forEach((yearMonth) => {
          const colIndex = monthColumnMap[yearMonth];
          if (colIndex !== undefined) {
            const beforeVal = parseFloat(String(beforeRow[colIndex] ?? "").replace(/,/g, ""));
            if (isNaN(beforeVal)) { beforeMonthlyData[yearMonth] = 0; errorCells.add(`${newItemId}:${yearMonth}:before`); }
            else beforeMonthlyData[yearMonth] = beforeVal;

            if (afterRow) {
              const afterVal = parseFloat(String(afterRow[colIndex] ?? "").replace(/,/g, ""));
              if (isNaN(afterVal)) { afterMonthlyData[yearMonth] = 0; errorCells.add(`${newItemId}:${yearMonth}:after`); }
              else afterMonthlyData[yearMonth] = afterVal;
            }
          } else {
            beforeMonthlyData[yearMonth] = 0;
            afterMonthlyData[yearMonth] = 0;
          }
        });

        if (hasItemError) errorCells.add(`${newItemId}:item`);

        newItems.push({
          id: newItemId,
          itemType,
          customItemName: customItemNameVal,
          unitType: itemType === "custom" ? "custom" : (optionsList.find(opt => opt.value === itemType)?.unit === "pcs" ? "pcs" : "currency"),
          customUnit: customUnitVal,
          beforeMonthlyData,
          afterMonthlyData,
        });
        rowIndex += 2;
      }

      if (group === "indicator") {
        setIndicatorItems(newItems);
        setIndicatorErrorCells(errorCells);
      } else {
        const itemsWithFlag = ensureRequiredCostItems(newItems).map(item => {
          if (item.itemType !== "targetEquipmentDepreciation") return item;
          const prevItem = costItems.find(prev => prev.itemType === "targetEquipmentDepreciation");
          const roundVal = (v: number) => Math.round(v * 100) / 100;
          const allMonths = Object.keys({ ...prevItem?.beforeMonthlyData, ...item.beforeMonthlyData });
          const beforeChanged = prevItem
            ? allMonths.some(m => roundVal(item.beforeMonthlyData[m] ?? 0) !== roundVal(prevItem.beforeMonthlyData[m] ?? 0))
            : false;
          const afterAllMonths = Object.keys({ ...prevItem?.afterMonthlyData, ...item.afterMonthlyData });
          const afterChanged = prevItem
            ? afterAllMonths.some(m => roundVal(item.afterMonthlyData[m] ?? 0) !== roundVal(prevItem.afterMonthlyData[m] ?? 0))
            : false;
          return { ...item, isManuallyEditedBefore: beforeChanged, isManuallyEditedAfter: afterChanged };
        });
        setCostItems(itemsWithFlag);
        setCostErrorCells(errorCells);
      }

      if (errorCells.size > 0) {
        toast({
          title: TITLE,
          description: t('components.UpdateSummaryInputPopover.message.dataLinkCompleteWithError', { count: errorCells.size }),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: TITLE,
          description: t('components.UpdateSummaryInputPopover.message.dataLinkComplete'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error("handleGroupDataLink Error.", error);
      toast({
        title: TITLE,
        description: t('components.UpdateSummaryInputPopover.message.dataLinkFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsGsLoading(false);
    }
  };

  /**
   * 一時保存ハンドラー
   */
  const handleTempSave = () => {
    const nameId = (productionLineName ?? "unknown").replace(/[\\/:*?"<>|]/g, "_");
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const json = JSON.stringify({ screenName: "UpdateSummaryInputPopover", nameId, savedAt: new Date().toISOString(), data: { localUpdateSummary, inputValues, isAIAnalyzed, isAIKnowledgeAnalyzed, isAIAdviceConfirmed, indicatorItems, costItems, selectedCostCurrency } }, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `UpdateSummaryInputPopover_${nameId}_${dateStr}.data`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: TITLE, description: t('components.UpdateSummaryInputPopover.message.tempSaved'), status: "success", duration: 3000, isClosable: true });
  };

  /**
   * 一時保存読み込みハンドラー
   */
  const handleTempLoad = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".data";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const parsed = JSON.parse(ev.target?.result as string);
          if (parsed.screenName !== "UpdateSummaryInputPopover") {
            toast({ title: TITLE, description: t('components.UpdateSummaryInputPopover.message.tempLoadScreenMismatch'), status: "error", duration: 4000, isClosable: true });
            return;
          }
          setLocalUpdateSummary(parsed.data.localUpdateSummary);
          setInputValues(parsed.data.inputValues);
          if (parsed.data.isAIAnalyzed !== undefined) setIsAIAnalyzed(parsed.data.isAIAnalyzed);
          if (parsed.data.isAIKnowledgeAnalyzed !== undefined) setIsAIKnowledgeAnalyzed(parsed.data.isAIKnowledgeAnalyzed);
          if (parsed.data.isAIAdviceConfirmed !== undefined) setIsAIAdviceConfirmed(parsed.data.isAIAdviceConfirmed);
          if (parsed.data.indicatorItems !== undefined) setIndicatorItems(parsed.data.indicatorItems);
          if (parsed.data.costItems !== undefined) setCostItems(parsed.data.costItems);
          if (parsed.data.selectedCostCurrency !== undefined) setSelectedCostCurrency(parsed.data.selectedCostCurrency);
          toast({ title: TITLE, description: t('components.UpdateSummaryInputPopover.message.tempLoaded'), status: "success", duration: 3000, isClosable: true });
        } catch {
          toast({ title: TITLE, description: t('components.UpdateSummaryInputPopover.message.tempLoadFailed'), status: "error", duration: 4000, isClosable: true });
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  /**
   * 保存ハンドラー
   */
  const handleSave = async () => {
    let saveData: UpdateSummaryInputData;

    if (isSimpleMode) {
      // 簡易モード：profitLoss は非表示で入力しないため保存しない
      saveData = {
        ...localUpdateSummary,
        profitLoss: undefined,
        indicatorItems: indicatorItems,
        costItems: costItems,
        costCurrencyId: selectedCostCurrency,
      };
    } else {
      // 詳細モード：スクラップ率・スクラップ費用を計算して保存データに追加
      const productionQuantity = localUpdateSummary.profitLoss?.productionQuantity ?? 0;
      const cumulativeCost = localUpdateSummary.cumulativeCost ?? 0;

      // 直近実績
      const recentActualScrapRate = 100 - (currentPartNoData.recentActual?.yield ?? 0);
      const recentActualScrapCost = Math.round(productionQuantity * (recentActualScrapRate / 100) * cumulativeCost);

      // 想定
      const theoreticalScrapRate = 100 - (currentPartNoData.theoretical?.yield ?? 0);
      const theoreticalScrapCost = Math.round(productionQuantity * (theoreticalScrapRate / 100) * cumulativeCost);

      // 目論見
      const investmentPlanScrapRate = 100 - (currentPartNoData.investmentPlan?.yield ?? 0);
      const investmentPlanScrapCost = Math.round(productionQuantity * (investmentPlanScrapRate / 100) * cumulativeCost);

      saveData = {
        ...localUpdateSummary,
        profitLoss: {
          ...localUpdateSummary.profitLoss,
          recentActual: {
            ...localUpdateSummary.profitLoss?.recentActual,
            scrapRate: recentActualScrapRate,
            scrapCost: recentActualScrapCost,
          },
          theoretical: {
            ...localUpdateSummary.profitLoss?.theoretical,
            scrapRate: theoreticalScrapRate,
            scrapCost: theoreticalScrapCost,
          },
          investmentPlan: {
            ...localUpdateSummary.profitLoss?.investmentPlan,
            scrapRate: investmentPlanScrapRate,
            scrapCost: investmentPlanScrapCost,
          },
        },
        indicatorItems: indicatorItems,
        costItems: costItems,
        costCurrencyId: selectedCostCurrency,
      };
    }

    // 保存処理
    const result = await onSave(saveData);
    if (result) {
      setIsOpen(false);
    }
  }

  /**
   * リセットハンドラー（閉じるときにDBデータまたは初期値に戻す）
   */
  const handleReset = () => {
    const sourceData = updateSummaryData ?? defaultUpdateSummary;
    let data = { ...sourceData };
    const originalSelectedPartNo = sourceData.selectedPartNo;

    // 全PartNoの計算値を算出
    const allPartNos = Object.keys(sourceData.productionCapacityByPartNo || {});
    allPartNos.forEach((partNo) => {
      data = { ...data, selectedPartNo: partNo };
      ["recentActual", "theoretical", "investmentPlan"].forEach((stateType) => {
        data = calculateUpdateSummary(data, stateType as any);
      });
    });

    // selectedPartNoを元に戻す
    data = { ...data, selectedPartNo: originalSelectedPartNo };
    setLocalUpdateSummary(data);

    // 選択中のPartNoのデータを取得
    const selectedPartNo = data.selectedPartNo;
    const partNoData = selectedPartNo
      ? data.productionCapacityByPartNo?.[selectedPartNo]
      : null;

    setInputValues({
      // 基本情報
      "operatingDays": String(data.operatingDays ?? 0),
      "partsCount": String(data.partsCount ?? 1),
      "cumulativeCost": String(data.cumulativeCost ?? 0),
      // 生産能力
      "recentActual.yield": (partNoData?.recentActual?.yield ?? 0).toFixed(2),
      "theoretical.yield": (partNoData?.theoretical?.yield ?? 0).toFixed(2),
      "investmentPlan.yield": (partNoData?.investmentPlan?.yield ?? 0).toFixed(2),
      "recentActual.nonWorkingTime": (partNoData?.recentActual?.nonWorkingTime ?? 0).toFixed(2),
      "theoretical.nonWorkingTime": (partNoData?.theoretical?.nonWorkingTime ?? 0).toFixed(2),
      "investmentPlan.nonWorkingTime": (partNoData?.investmentPlan?.nonWorkingTime ?? 0).toFixed(2),
      "recentActual.machineTrouble": (partNoData?.recentActual?.machineTrouble ?? 0).toFixed(2),
      "theoretical.machineTrouble": (partNoData?.theoretical?.machineTrouble ?? 0).toFixed(2),
      "investmentPlan.machineTrouble": (partNoData?.investmentPlan?.machineTrouble ?? 0).toFixed(2),
      "recentActual.maintenance": (partNoData?.recentActual?.maintenance ?? 0).toFixed(2),
      "theoretical.maintenance": (partNoData?.theoretical?.maintenance ?? 0).toFixed(2),
      "investmentPlan.maintenance": (partNoData?.investmentPlan?.maintenance ?? 0).toFixed(2),
      "recentActual.cycleTime": (partNoData?.recentActual?.cycleTime ?? 0).toFixed(1),
      "theoretical.cycleTime": (partNoData?.theoretical?.cycleTime ?? 0).toFixed(1),
      "investmentPlan.cycleTime": (partNoData?.investmentPlan?.cycleTime ?? 0).toFixed(1),
      "recentActual.personnelPerDay": (partNoData?.recentActual?.personnelPerDay ?? 0).toFixed(1),
      "theoretical.personnelPerDay": (partNoData?.theoretical?.personnelPerDay ?? 0).toFixed(1),
      "investmentPlan.personnelPerDay": (partNoData?.investmentPlan?.personnelPerDay ?? 0).toFixed(1),
      // 損益
      "profitLoss.productionQuantity": String(data.profitLoss?.productionQuantity ?? 0),
      "profitLoss.recentActual.repairCost": String(data.profitLoss?.recentActual?.repairCost ?? 0),
      "profitLoss.theoretical.repairCost": String(data.profitLoss?.theoretical?.repairCost ?? 0),
      "profitLoss.investmentPlan.repairCost": String(data.profitLoss?.investmentPlan?.repairCost ?? 0),
      "profitLoss.recentActual.consumablesCost": String(data.profitLoss?.recentActual?.consumablesCost ?? 0),
      "profitLoss.theoretical.consumablesCost": String(data.profitLoss?.theoretical?.consumablesCost ?? 0),
      "profitLoss.investmentPlan.consumablesCost": String(data.profitLoss?.investmentPlan?.consumablesCost ?? 0),
    });
    setErrors(JSON.parse(JSON.stringify(defaultErrors)));
    setErrorsByPartNo({});
    // 指標グループと費用グループのリセット
    setIndicatorItems(ensureRequiredIndicatorItems(sourceData.indicatorItems || []));
    setCostItems(ensureRequiredCostItems(sourceData.costItems || []));
    setSelectedIndicatorItemType("");
    setSelectedCostItemType("");
    setIsAIModalOpen(false);
    setIsAIAnalyzed(false);
    setIsKnowledgeModalOpen(false);
    setIsAIKnowledgeAnalyzed(false);
    setIsAIAdviceConfirmed(false);
    setIsOpen(false);
  };

  /**
   * 数値フォーマット
   */
  const formatNumberLocal = (value: number | undefined): string => {
    if (value === undefined || value === null) return "";
    return value.toLocaleString();
  };

  // エラーチェック
  // 関数宣言（エラー情報設定）
  const changeErrors = (name: string, errorMessage: string) => {
    setErrors((prevState) => ({
      ...prevState,
      [name]: { isError: true, errorMessage: `${errorMessage}` },
    }));
  };

  /**
    * バリデーションを行う。
    */
  const validate = () => {
    // 必須入力チェック
    const required = (value: unknown) => {
      if (value === null || value === undefined) return false;

      const str = String(value).trim();
      return str !== "" && str !== "0" && str !== "０";
    };

    // エラー情報初期化
    setErrors(JSON.parse(JSON.stringify(defaultErrors)));

    let isError = false;

    // 固定資産番号
    if (!required(localUpdateSummary?.fixedAssetNumber ?? "")) {
      changeErrors("fixedAssetNumber", t('components.UpdateSummaryInputPopover.validation.fixedAssetNumberRequired'));
      isError = true;
    }

    // 稼働日数
    if (!required(String(localUpdateSummary.operatingDays) ?? "")) {
      changeErrors("operatingDays", t('components.UpdateSummaryInputPopover.validation.operatingDaysRequired'));
      isError = true;
    }

    // 全PartNoの生産能力チェック（PartNoごとのエラー管理）
    const missingPartNos: string[] = [];
    const newErrorsByPartNo: { [partNo: string]: ProductionCapacityErrors } = {};

    // partNoListが空でない場合、全PartNoで必須項目をチェック
    if (partNoList.length > 0) {
      for (const partNoItem of partNoList) {
        const partNo = partNoItem.partNo;
        const partNoData = localUpdateSummary.productionCapacityByPartNo?.[partNo];

        // このPartNoのエラーを初期化
        const partNoErrors: ProductionCapacityErrors = JSON.parse(JSON.stringify(defaultPartNoErrors));
        let hasError = false;

        if (!partNoData) {
          // PartNoのデータが存在しない場合、全項目エラー
          partNoErrors.recentActualYield = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.yieldRequired') };
          partNoErrors.theoreticalYield = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.yieldRequired') };
          partNoErrors.investmentPlanYield = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.yieldRequired') };
          partNoErrors.recentActualCycleTime = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.cycleTimeRequired') };
          partNoErrors.theoreticalCycleTime = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.cycleTimeRequired') };
          partNoErrors.investmentPlanCycleTime = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.cycleTimeRequired') };
          partNoErrors.recentActualPersonnelPerDay = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.personnelPerDayRequired') };
          partNoErrors.theoreticalPersonnelPerDay = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.personnelPerDayRequired') };
          partNoErrors.investmentPlanPersonnelPerDay = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.personnelPerDayRequired') };
          missingPartNos.push(partNo);
          hasError = true;
        } else {
          // 歩留チェック
          if (!required(partNoData.recentActual?.yield ?? "")) {
            partNoErrors.recentActualYield = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.yieldRequired') };
            hasError = true;
          }
          if (!required(partNoData.theoretical?.yield ?? "")) {
            partNoErrors.theoreticalYield = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.yieldRequired') };
            hasError = true;
          }
          if (!required(partNoData.investmentPlan?.yield ?? "")) {
            partNoErrors.investmentPlanYield = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.yieldRequired') };
            hasError = true;
          }

          // サイクルタイムチェック
          if (!required(partNoData.recentActual?.cycleTime ?? "")) {
            partNoErrors.recentActualCycleTime = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.cycleTimeRequired') };
            hasError = true;
          }
          if (!required(partNoData.theoretical?.cycleTime ?? "")) {
            partNoErrors.theoreticalCycleTime = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.cycleTimeRequired') };
            hasError = true;
          }
          if (!required(partNoData.investmentPlan?.cycleTime ?? "")) {
            partNoErrors.investmentPlanCycleTime = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.cycleTimeRequired') };
            hasError = true;
          }

          // 1日あたり必要人員数チェック
          if (!required(partNoData.recentActual?.personnelPerDay ?? "")) {
            partNoErrors.recentActualPersonnelPerDay = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.personnelPerDayRequired') };
            hasError = true;
          }
          if (!required(partNoData.theoretical?.personnelPerDay ?? "")) {
            partNoErrors.theoreticalPersonnelPerDay = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.personnelPerDayRequired') };
            hasError = true;
          }
          if (!required(partNoData.investmentPlan?.personnelPerDay ?? "")) {
            partNoErrors.investmentPlanPersonnelPerDay = { isError: true, errorMessage: t('components.UpdateSummaryInputPopover.validation.personnelPerDayRequired') };
            hasError = true;
          }

          if (hasError) {
            missingPartNos.push(partNo);
          }
        }

        newErrorsByPartNo[partNo] = partNoErrors;
        if (hasError) {
          isError = true;
        }
      }

      // PartNoごとのエラーを設定
      setErrorsByPartNo(newErrorsByPartNo);

      if (missingPartNos.length > 0) {
        changeErrors("productionCapacity", `${t('components.UpdateSummaryInputPopover.validation.partNoMissingInput')}${missingPartNos.join(", ")}`);
      }
    } else {
      // PartNoリストが空の場合はエラーをクリア
      setErrorsByPartNo({});
    }

    // 更新をする理由
    if (!required(localUpdateSummary.updateReason ?? "")) {
      changeErrors("updateReason", t('components.UpdateSummaryInputPopover.validation.updateReasonRequired'));
      isError = true;
    }

    // 設備あたり生産性向上対策
    if (!required(localUpdateSummary.productionPerEquipment ?? "")) {
      changeErrors("productionPerEquipment", t('components.UpdateSummaryInputPopover.validation.productionPerEquipmentRequired'));
      isError = true;
    }

    // 1人あたり生産性向上対策
    if (!required(localUpdateSummary.productionPerPerson ?? "")) {
      changeErrors("productionPerPerson", t('components.UpdateSummaryInputPopover.validation.productionPerPersonRequired'));
      isError = true;
    }

    // 非稼働対策
    if (!required(localUpdateSummary.nonOperationMeasures ?? "")) {
      changeErrors("nonOperationMeasures", t('components.UpdateSummaryInputPopover.validation.nonOperationMeasuresRequired'));
      isError = true;
    }

    // 非加工対策
    if (!required(localUpdateSummary.nonProcessingMeasures ?? "")) {
      changeErrors("nonProcessingMeasures", t('components.UpdateSummaryInputPopover.validation.nonProcessingMeasuresRequired'));
      isError = true;
    }

    // 現行設備の処置方法
    if (!required(localUpdateSummary.currentEquipmentHandling ?? "")) {
      changeErrors("currentEquipmentHandling", t('components.UpdateSummaryInputPopover.validation.currentEquipmentHandlingRequired'));
      isError = true;
    }

    // 労働時間
    if (!required(localUpdateSummary?.workTime === DEFAULT_TIME ? "" : (localUpdateSummary?.workTime ?? ""))) {
      changeErrors("workTime", t('components.UpdateSummaryInputPopover.validation.workTimeRequired'));
      isError = true;
    }
    // 勤務体制
    if (!required(localUpdateSummary?.shiftSystem ?? "")) {
      changeErrors("shiftSystem", t('components.UpdateSummaryInputPopover.validation.shiftSystemRequired'));
      isError = true;
    }
    // 当該工程までの累積原価
    if (!required(String(localUpdateSummary?.cumulativeCost ?? ""))) {
      changeErrors("cumulativeCost", t('components.UpdateSummaryInputPopover.validation.cumulativeCostRequired'));
      isError = true;
    }

    // 生産数（簡易モード時は profitLoss を使用しないため検証スキップ）
    if (!isSimpleMode && !required(String(localUpdateSummary.profitLoss?.productionQuantity ?? ""))) {
      changeErrors("productionQuantity", t('components.UpdateSummaryInputPopover.validation.productionQuantityRequired'));
      isError = true;
    }

    // 費用グループの通貨（簡易モードかつ費用項目が1つ以上ある場合は必須）
    if (isSimpleMode && costItems.length > 0 && !selectedCostCurrency) {
      changeErrors("costCurrency", t('components.UpdateSummaryInputPopover.validation.costCurrencyRequired'));
      isError = true;
    }

    return !isError;
  }

  // Null,Undefined,∞をチェック
  const safeNumber = (value: any, defaultValue = 0): number => {
    const num = Number(value);
    if (value == null || !Number.isFinite(num)) {
      return defaultValue;
    }
    return num;
  };

  /**
   * 現在の工程に紐づくPartNoの一覧を取得
   */
  const partNoList = useMemo((): CycleTimeInSecondByPartNoType[] => {
    if (!productionLineId || !processCodeData) return [];

    const selectedProcess = processCodeData.find(
      (p) => p.uniqueProcessId === productionLineId
    );

    if (!selectedProcess?.uniqueProcessCycleTimeInSecondByPartNo) return [];

    try {
      const parsed = JSON.parse(selectedProcess.uniqueProcessCycleTimeInSecondByPartNo);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [productionLineId, processCodeData]);

  /**
   * 選択中のPartNoのエラー情報を取得
   */
  const currentPartNoErrors = useMemo((): ProductionCapacityErrors => {
    const selectedPartNo = localUpdateSummary.selectedPartNo;
    if (!selectedPartNo) return defaultPartNoErrors;
    return errorsByPartNo[selectedPartNo] || defaultPartNoErrors;
  }, [localUpdateSummary.selectedPartNo, errorsByPartNo]);

  /**
   * PartNo選択変更ハンドラー
   * - 現在のPartNoのデータを保存
   * - 新しいPartNoのデータを読み込む
   * - inputValuesを更新
   */
  const handlePartNoChange = (newPartNo: string) => {
    setLocalUpdateSummary((prev) => {
      let updated = { ...prev };

      // 新しいPartNoのデータを取得（既存or新規作成）
      let newPartNoData = prev.productionCapacityByPartNo?.[newPartNo];
      if (!newPartNoData) {
        // 新規PartNoの場合はデフォルトデータを作成
        newPartNoData = {
          recentActual: { ...defaultProductionCapacityData },
          theoretical: { ...defaultProductionCapacityData },
          investmentPlan: {
            ...defaultProductionCapacityData,
            yield: productionLineData?.yield || 0,
            cycleTime: productionLineData?.cycleTime || undefined,
          },
        };
        // 新規PartNoデータを保存
        updated.productionCapacityByPartNo = {
          ...prev.productionCapacityByPartNo,
          [newPartNo]: newPartNoData,
        };
      }

      // 選択PartNoを更新
      updated.selectedPartNo = newPartNo;

      // 計算値を算出
      ["recentActual", "theoretical", "investmentPlan"].forEach((stateType) => {
        updated = calculateUpdateSummary(updated, stateType as any);
      });

      return updated;
    });

    // inputValuesも更新
    if (newPartNo) {
      const partNoData = localUpdateSummary.productionCapacityByPartNo?.[newPartNo];
      if (partNoData) {
        setInputValues(prev => ({
          ...prev,
          "recentActual.yield": (partNoData.recentActual?.yield ?? 0).toFixed(2),
          "theoretical.yield": (partNoData.theoretical?.yield ?? 0).toFixed(2),
          "investmentPlan.yield": (partNoData.investmentPlan?.yield ?? (productionLineData?.yield || 0)).toFixed(2),
          "recentActual.nonWorkingTime": (partNoData.recentActual?.nonWorkingTime ?? 0).toFixed(2),
          "theoretical.nonWorkingTime": (partNoData.theoretical?.nonWorkingTime ?? 0).toFixed(2),
          "investmentPlan.nonWorkingTime": (partNoData.investmentPlan?.nonWorkingTime ?? 0).toFixed(2),
          "recentActual.machineTrouble": (partNoData.recentActual?.machineTrouble ?? 0).toFixed(2),
          "theoretical.machineTrouble": (partNoData.theoretical?.machineTrouble ?? 0).toFixed(2),
          "investmentPlan.machineTrouble": (partNoData.investmentPlan?.machineTrouble ?? 0).toFixed(2),
          "recentActual.maintenance": (partNoData.recentActual?.maintenance ?? 0).toFixed(2),
          "theoretical.maintenance": (partNoData.theoretical?.maintenance ?? 0).toFixed(2),
          "investmentPlan.maintenance": (partNoData.investmentPlan?.maintenance ?? 0).toFixed(2),
          "recentActual.cycleTime": (partNoData.recentActual?.cycleTime ?? 0).toFixed(1),
          "theoretical.cycleTime": (partNoData.theoretical?.cycleTime ?? 0).toFixed(1),
          "investmentPlan.cycleTime": (partNoData.investmentPlan?.cycleTime ?? (productionLineData?.cycleTime || 0)).toFixed(1),
          "recentActual.personnelPerDay": (partNoData.recentActual?.personnelPerDay ?? 0).toFixed(1),
          "theoretical.personnelPerDay": (partNoData.theoretical?.personnelPerDay ?? 0).toFixed(1),
          "investmentPlan.personnelPerDay": (partNoData.investmentPlan?.personnelPerDay ?? 0).toFixed(1),
        }));
      } else {
        // 新規PartNoの場合はデフォルト値を設定
        setInputValues(prev => ({
          ...prev,
          "recentActual.yield": "0.00",
          "theoretical.yield": "0.00",
          "investmentPlan.yield": (productionLineData?.yield || 0).toFixed(2),
          "recentActual.nonWorkingTime": "0.00",
          "theoretical.nonWorkingTime": "0.00",
          "investmentPlan.nonWorkingTime": "0.00",
          "recentActual.machineTrouble": "0.00",
          "theoretical.machineTrouble": "0.00",
          "investmentPlan.machineTrouble": "0.00",
          "recentActual.maintenance": "0.00",
          "theoretical.maintenance": "0.00",
          "investmentPlan.maintenance": "0.00",
          "recentActual.cycleTime": "0.0",
          "theoretical.cycleTime": "0.0",
          "investmentPlan.cycleTime": (productionLineData?.cycleTime || 0).toFixed(1),
          "recentActual.personnelPerDay": "0.0",
          "theoretical.personnelPerDay": "0.0",
          "investmentPlan.personnelPerDay": "0.0",
        }));
      }
    }
  };

  // -----------------------------
  // useEffect(値更新)
  // -----------------------------

  // 労働時間、シフト、稼働日数更新時再計算（再描画の為）
  useEffect(() => {
    setLocalUpdateSummary((prev) => {
      let updated = { ...prev };
      const originalSelectedPartNo = prev.selectedPartNo;

      // 全PartNoの計算値を再算出
      const allPartNos = Object.keys(prev.productionCapacityByPartNo || {});
      allPartNos.forEach((partNo) => {
        updated = { ...updated, selectedPartNo: partNo };
        ["recentActual", "theoretical", "investmentPlan"].forEach((stateType) => {
          updated = calculateUpdateSummary(updated, stateType as any);
        });
      });

      // selectedPartNoを元に戻す
      return { ...updated, selectedPartNo: originalSelectedPartNo };
    });
  }, [localUpdateSummary.workTime, localUpdateSummary.shiftSystem, localUpdateSummary.operatingDays]);




  /**
   * レンダリング
   */
  return (
    <>
      <Button
        size="sm"
        onClick={() => setIsOpen(true)}
        m={1}
      >
        {t('components.UpdateSummaryInputPopover.button.summaryInput')}
      </Button>

      <Modal isOpen={isOpen} onClose={handleReset} scrollBehavior="inside" closeOnEsc={!isAIModalOpen && !isKnowledgeModalOpen}>
        <ModalOverlay />
        <ModalContent minW={1500}>
          <ModalHeader>
            <HStack spacing={1} align="center">
              <Text>{TITLE + t('components.UpdateSummaryInputPopover.note.focusOnRelatedProcess')}</Text>
              <Popover placement="auto">
                <PopoverTrigger>
                  <Button
                    style={{ background: 'none', border: 'none', padding: 0 }}
                    minW="auto"
                    h="auto"
                    ml={1}
                    mt={1}
                  >
                    <FaRegQuestionCircle size="16px" cursor="pointer" />
                  </Button>
                </PopoverTrigger>
                <Portal>
                  <PopoverContent width="fit-content" maxW="750px" bgColor="#fffff0">
                    <PopoverArrow />
                    <PopoverBody>
                      <Text fontSize="sm" whiteSpace="pre-wrap">{t('components.UpdateSummaryInputPopover.label.headerTooltip')}</Text>
                    </PopoverBody>
                  </PopoverContent>
                </Portal>
              </Popover>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              {/* NumberIncrementStepper/NumberDecrementStepperはネイティブbuttonではないためfieldsetのdisabledが効かず、pointerEvents:'none'で明示的にクリック不可にする */}
              <fieldset disabled={!pagePermissions?.isEditable} style={!pagePermissions?.isEditable ? { pointerEvents: 'none' } : undefined}>
                {/* 基本情報 */}
                <Box>
                  <VStack align="stretch" spacing={2}>
                    <FormControl mb={2}>
                      <HStack spacing={2} alignItems="center">
                        <Text textAlign="left" fontWeight="semibold">
                          ProcessCode:
                        </Text>
                        <Text>{productionLineId}</Text>
                      </HStack>
                      <HStack spacing={2} alignItems="center">
                        <Text textAlign="left" fontWeight="semibold">
                          ItemNo:
                        </Text>
                        <Text>{productionLineName}</Text>
                      </HStack>
                    </FormControl>
                    <Text fontWeight="bold" mb={1}>{t('components.UpdateSummaryInputPopover.section.basicInfo')}</Text>
                    <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4} fontSize="sm">
                      <VStack align="stretch" spacing={2}>
                        <HStack spacing={4} alignItems="flex-start" >
                          <FormControl width="50%" isRequired isInvalid={!!errors.fixedAssetNumber.isError}>
                            <FormLabel fontSize="sm">{t('components.UpdateSummaryInputPopover.label.fixedAssetNumber')}</FormLabel>
                            <Input
                              fontSize="sm"
                              value={localUpdateSummary.fixedAssetNumber || ""}
                              onChange={(e) => handleUpdate("fixedAssetNumber", e.target.value)}
                            />
                            <FormErrorMessage>{errors.fixedAssetNumber.errorMessage}</FormErrorMessage>
                          </FormControl>
                        </HStack>
                        <HStack spacing={4} alignItems="flex-start">
                          <FormControl w="165px" isRequired isInvalid={!!errors.operatingDays.isError}>
                            <FormLabel fontSize="sm">{t('components.UpdateSummaryInputPopover.label.operatingDays')}</FormLabel>
                            <NumberInput
                              fontSize="sm"
                              value={inputValues["operatingDays"]}
                              onChange={(value) => handleOperatingDaysInputChange(value)}
                              onBlur={() => handleOperatingDaysInputBlur()}
                              min={0}
                              max={maxOperatingDays}
                            >
                              <NumberInputField placeholder={t('components.UpdateSummaryInputPopover.placeholder.operatingDays')} />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                            <FormErrorMessage>{errors.operatingDays.errorMessage}</FormErrorMessage>
                          </FormControl>
                          <FormControl w="120px" isRequired isInvalid={!!errors.workTime.isError}>
                            <FormLabel fontSize="sm">{t('components.UpdateSummaryInputPopover.label.workTime')}</FormLabel>
                            <CeasTimePicker
                              isInvalid={!!errors.workTime.isError}
                              value={localUpdateSummary.workTime || DEFAULT_TIME}
                              onChange={(value) => {
                                handleUpdate("workTime", value);
                                // 労働時間変更時に各状態を再計算
                                ["recentActual", "theoretical", "investmentPlan"].forEach((stateType) => {
                                  setLocalUpdateSummary((prev) =>
                                    calculateUpdateSummary({ ...prev, operatingDays: localUpdateSummary.operatingDays }, stateType as any)
                                  );
                                });
                              }}
                            />
                            <FormErrorMessage>{errors.workTime.errorMessage}</FormErrorMessage>
                          </FormControl>
                          <FormControl w="120px" isRequired isInvalid={!!errors.shiftSystem.isError}>
                            <FormLabel fontSize="sm">{t('components.UpdateSummaryInputPopover.label.shiftSystem')}</FormLabel>
                            <Select
                              fontSize="sm"
                              value={localUpdateSummary.shiftSystem || ShiftSystem.OneShiftSystem.code}
                              onChange={(e) => {
                                handleUpdate("shiftSystem", e.target.value);
                                // 勤務体制変更時に各状態を再計算
                                ["recentActual", "theoretical", "investmentPlan"].forEach((stateType) => {
                                  setLocalUpdateSummary((prev) =>
                                    calculateUpdateSummary({ ...prev, operatingDays: localUpdateSummary.operatingDays }, stateType as any)
                                  );
                                });
                              }}
                            >
                              {Object.entries(codeToNameMap(ShiftSystem)).map(([code, name]) => (
                                <option key={code} value={code}>
                                  {name}
                                </option>
                              ))}
                            </Select>
                            <FormErrorMessage>{errors.shiftSystem.errorMessage}</FormErrorMessage>
                          </FormControl>
                        </HStack>
                        <Text fontSize="xs" color="gray.600">
                          {t('components.UpdateSummaryInputPopover.note.enterRecentThreeMonthAverage')}
                        </Text>
                        <HStack spacing={4} align="flex-start">
                          <FormControl w="180px" isInvalid={!!errors.partsCount.isError}>
                            <FormLabel fontSize="sm">{t('components.UpdateSummaryInputPopover.label.partsCount')}</FormLabel>
                            <HStack spacing={1}>
                              <NumberInput
                                size="sm"
                                width="120px"
                                min={1}
                                value={inputValues["partsCount"]}
                                onChange={(value) => handlePartsCountInputChange(value)}
                                onBlur={() => handlePartsCountInputBlur()}
                              >
                                <NumberInputField placeholder={t('components.UpdateSummaryInputPopover.placeholder.partsCount')} textAlign="right" />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                              <Text fontSize="xs">pcs</Text>
                            </HStack>
                            <FormErrorMessage>{errors.partsCount.errorMessage}</FormErrorMessage>
                          </FormControl>
                          <FormControl w="200px" isRequired isInvalid={!!errors.cumulativeCost.isError}>
                            <FormLabel fontSize="sm">{t('components.UpdateSummaryInputPopover.label.cumulativeCost')}</FormLabel>
                            <HStack spacing={1}>
                              <NumberInput
                                size="sm"
                                width="150px"
                                min={0}
                                value={inputValues["cumulativeCost"]}
                                onChange={(value) => handleCumulativeCostInputChange(value)}
                                onBlur={() => handleCumulativeCostInputBlur()}
                              >
                                <NumberInputField placeholder={t('components.UpdateSummaryInputPopover.placeholder.cumulativeCost')} textAlign="right" />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                              <Text fontSize="xs">JPY</Text>
                            </HStack>
                            <FormErrorMessage>{errors.cumulativeCost.errorMessage}</FormErrorMessage>
                          </FormControl>
                        </HStack>
                      </VStack>
                    </Box>
                  </VStack>
                </Box>
              </fieldset>

              {/* 生産能力データテーブル（PartNoは複数パーツのデータ参照のため常に活性化） */}
              <Box>
                <Text fontWeight="bold" mb={3}>{t('components.UpdateSummaryInputPopover.section.productionCapacity')}</Text>
                <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4} fontSize="sm">
                  <HStack spacing={2} mb={3} justifyContent="flex-start">
                    <Text fontSize="sm" fontWeight="medium">{t('components.UpdateSummaryInputPopover.label.partNo')}</Text>
                    <Select
                      size="sm"
                      w="200px"
                      value={localUpdateSummary.selectedPartNo || ""}
                      onChange={(e) => handlePartNoChange(e.target.value)}
                    >
                      <option value="">{t('components.UpdateSummaryInputPopover.placeholder.partNoSelect')}</option>
                      {partNoList.map((item) => (
                        <option key={item.partNo} value={item.partNo}>
                          {resolveDisplayPartNoByKey(item.partNo, partNoData)}
                        </option>
                      ))}
                    </Select>
                  </HStack>
                  <fieldset disabled={!pagePermissions?.isEditable} style={!pagePermissions?.isEditable ? { pointerEvents: 'none' } : undefined}>
                    {errors.productionCapacity?.isError && (
                      <Text color="red.500" fontSize="sm" mb={3}>
                        {errors.productionCapacity.errorMessage}
                      </Text>
                    )}
                    {localUpdateSummary.selectedPartNo && (
                      <Table variant="simple" size="sm" border="1px solid" borderColor="gray.200" sx={{
                        tableLayout: "fixed",
                        borderCollapse: "collapse",
                        "& td, & th": {
                          borderBottom: "1px solid",
                          borderBottomColor: "gray.200"
                        }
                      }}>
                        <colgroup>
                          <col style={{ width: "150px" }} />
                          <col style={{ width: "150px" }} />
                          <col style={{ width: "150px" }} />
                          <col style={{ width: "150px" }} />
                          <col style={{ width: "150px" }} />
                        </colgroup>
                        <Thead bg="gray.100">
                          <Tr>
                            <Th rowSpan={2} borderRight="1px solid" borderColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.item')}</Th>
                            <Th colSpan={2} textAlign="center" borderRight="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.current')}</Th>
                            <Th colSpan={1} textAlign="center" borderRight="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.investmentUpdate')}</Th>
                            <Th colSpan={1} textAlign="center" borderRight="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.difference')}</Th>
                          </Tr>
                          <Tr>
                            <Th borderRight="1px solid" borderColor="gray.200" textAlign="center">{t('components.UpdateSummaryInputPopover.tableHeader.recentThreeMonthAverage')}</Th>
                            <Th borderRight="1px solid" borderColor="gray.200" textAlign="center">{t('components.UpdateSummaryInputPopover.tableHeader.assumption')}</Th>
                            <Th borderRight="1px solid" borderColor="gray.200" textAlign="center">{t('components.UpdateSummaryInputPopover.tableHeader.plan')}</Th>
                            <Th borderRight="1px solid" borderColor="gray.200" textAlign="center">{t('components.UpdateSummaryInputPopover.tableHeader.actualVsPlan')}</Th>
                          </Tr>
                        </Thead>
                        <Tbody>

                          {/* 歩留 */}
                          <Tr h="48px">
                            <Td borderRight="1px solid" borderColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.yield')}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center" >
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center" isRequired isInvalid={!!currentPartNoErrors.recentActualYield.isError}>
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={100}
                                      precision={2}
                                      step={0.01}
                                      value={inputValues["recentActual.yield"]}
                                      onChange={(value) => handleInputChange("recentActual", "yield", value)}
                                      onBlur={() => handleInputBlur("recentActual", "yield")}
                                    >
                                      <NumberInputField placeholder="%" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                    <FormErrorMessage display="flex" justifyContent="center">
                                      {currentPartNoErrors.recentActualYield.errorMessage}
                                    </FormErrorMessage>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">%</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center" >
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center" isRequired isInvalid={!!currentPartNoErrors.theoreticalYield.isError}>
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={100}
                                      precision={2}
                                      step={0.01}
                                      value={inputValues["theoretical.yield"]}
                                      onChange={(value) => handleInputChange("theoretical", "yield", value)}
                                      onBlur={() => handleInputBlur("theoretical", "yield")}
                                    >
                                      <NumberInputField placeholder="%" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                    <FormErrorMessage display="flex" justifyContent="center">
                                      {currentPartNoErrors.theoreticalYield.errorMessage}
                                    </FormErrorMessage>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">%</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center" isRequired isInvalid={!!currentPartNoErrors.investmentPlanYield.isError}>
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={100}
                                      precision={2}
                                      step={0.01}
                                      value={inputValues["investmentPlan.yield"]}
                                      onChange={(value) => handleInputChange("investmentPlan", "yield", value)}
                                      onBlur={() => handleInputBlur("investmentPlan", "yield")}
                                    >
                                      <NumberInputField placeholder="%" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                    <FormErrorMessage display="flex" justifyContent="center">
                                      {currentPartNoErrors.investmentPlanYield.errorMessage}
                                    </FormErrorMessage>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">%</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td textAlign="center">
                              {safeNumber(
                                (currentPartNoData.investmentPlan?.yield ?? 0) - (currentPartNoData.recentActual?.yield ?? 0)
                              ).toFixed(2) + " %"}
                            </Td>
                          </Tr>

                          {/* 非稼働時間 */}
                          <Tr h="48px">
                            <Td borderRight="1px solid" borderColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.nonWorkingTime')}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center" >
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center">
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={24}
                                      precision={2}
                                      step={0.01}
                                      value={inputValues["recentActual.nonWorkingTime"]}
                                      onChange={(value) => handleInputChange("recentActual", "nonWorkingTime", value)}
                                      onBlur={() => handleInputBlur("recentActual", "nonWorkingTime")}
                                    >
                                      <NumberInputField placeholder="hr" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">hr/24hr</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center" >
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center">
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={24}
                                      precision={2}
                                      step={0.01}
                                      value={inputValues["theoretical.nonWorkingTime"]}
                                      onChange={(value) => handleInputChange("theoretical", "nonWorkingTime", value)}
                                      onBlur={() => handleInputBlur("theoretical", "nonWorkingTime")}
                                    >
                                      <NumberInputField placeholder="hr" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">hr/24hr</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center">
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={24}
                                      precision={2}
                                      step={0.01}
                                      value={inputValues["investmentPlan.nonWorkingTime"]}
                                      onChange={(value) => handleInputChange("investmentPlan", "nonWorkingTime", value)}
                                      onBlur={() => handleInputBlur("investmentPlan", "nonWorkingTime")}
                                    >
                                      <NumberInputField placeholder="hr" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">hr/24hr</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td textAlign="center">
                              {safeNumber(
                                (currentPartNoData.investmentPlan?.nonWorkingTime ?? 0) - (currentPartNoData.recentActual?.nonWorkingTime ?? 0)
                              ).toFixed(2) + " hr/24hr"}
                            </Td>
                          </Tr>

                          {/* 内）機械トラブル */}
                          <Tr h="48px">
                            <Td borderRight="1px solid" borderColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.machineTrouble')}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center" >
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center">
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={24}
                                      precision={2}
                                      step={0.01}
                                      value={inputValues["recentActual.machineTrouble"]}
                                      onChange={(value) => handleInputChange("recentActual", "machineTrouble", value)}
                                      onBlur={() => handleInputBlur("recentActual", "machineTrouble")}
                                    >
                                      <NumberInputField placeholder="hr" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">hr/24hr</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center" >
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center">
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={24}
                                      precision={2}
                                      step={0.01}
                                      value={inputValues["theoretical.machineTrouble"]}
                                      onChange={(value) => handleInputChange("theoretical", "machineTrouble", value)}
                                      onBlur={() => handleInputBlur("theoretical", "machineTrouble")}
                                    >
                                      <NumberInputField placeholder="hr" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">hr/24hr</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center">
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={24}
                                      precision={2}
                                      step={0.01}
                                      value={inputValues["investmentPlan.machineTrouble"]}
                                      onChange={(value) => handleInputChange("investmentPlan", "machineTrouble", value)}
                                      onBlur={() => handleInputBlur("investmentPlan", "machineTrouble")}
                                    >
                                      <NumberInputField placeholder="hr" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">hr/24hr</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td textAlign="center">
                              {safeNumber(
                                (currentPartNoData.investmentPlan?.machineTrouble ?? 0) - (currentPartNoData.recentActual?.machineTrouble ?? 0)
                              ).toFixed(2) + " hr/24hr"}
                            </Td>
                          </Tr>

                          {/* 内）保守・メンテナンス */}
                          <Tr h="48px">
                            <Td borderRight="1px solid" borderColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.maintenance')}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center" >
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center">
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={24}
                                      precision={2}
                                      step={0.01}
                                      value={inputValues["recentActual.maintenance"]}
                                      onChange={(value) => handleInputChange("recentActual", "maintenance", value)}
                                      onBlur={() => handleInputBlur("recentActual", "maintenance")}
                                    >
                                      <NumberInputField placeholder="hr" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">hr/24hr</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center" >
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center">
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={24}
                                      precision={2}
                                      step={0.01}
                                      value={inputValues["theoretical.maintenance"]}
                                      onChange={(value) => handleInputChange("theoretical", "maintenance", value)}
                                      onBlur={() => handleInputBlur("theoretical", "maintenance")}
                                    >
                                      <NumberInputField placeholder="hr" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">hr/24hr</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center">
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={24}
                                      precision={2}
                                      step={0.01}
                                      value={inputValues["investmentPlan.maintenance"]}
                                      onChange={(value) => handleInputChange("investmentPlan", "maintenance", value)}
                                      onBlur={() => handleInputBlur("investmentPlan", "maintenance")}
                                    >
                                      <NumberInputField placeholder="hr" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">hr/24hr</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td textAlign="center">
                              {safeNumber(
                                (currentPartNoData.investmentPlan?.maintenance ?? 0) - (currentPartNoData.recentActual?.maintenance ?? 0)
                              ).toFixed(2) + " hr/24hr"}
                            </Td>
                          </Tr>

                          {/* 稼働率 */}
                          <Tr bg="gray.50" h="48px">
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <HStack spacing={1}>
                                <Text>{t('components.UpdateSummaryInputPopover.tableHeader.operatingRate')}</Text>
                                <Popover placement="auto">
                                  <PopoverTrigger>
                                    <Button
                                      style={{ background: 'none', border: 'none', padding: 0 }}
                                      minW="auto"
                                      h="auto"
                                      ml={1}
                                    >
                                      <FaRegQuestionCircle size="16px" cursor="pointer" />
                                    </Button>
                                  </PopoverTrigger>
                                  <Portal>
                                    <PopoverContent width="fit-content" bgColor="#fffff0">
                                      <PopoverArrow />
                                      <PopoverBody>
                                        <Text fontSize="sm">{t('components.UpdateSummaryInputPopover.formula.operatingRate')}</Text>
                                      </PopoverBody>
                                    </PopoverContent>
                                  </Portal>
                                </Popover>
                              </HStack>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center" bg="gray.50">
                              {(((24 - (currentPartNoData.recentActual?.nonWorkingTime ?? 0)) / 24) * 100).toFixed(2) + " %"}
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center" bg="gray.50">
                              {(((24 - (currentPartNoData.theoretical?.nonWorkingTime ?? 0)) / 24) * 100).toFixed(2) + " %"}
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center" bg="gray.50">
                              {(((24 - (currentPartNoData.investmentPlan?.nonWorkingTime ?? 0)) / 24) * 100).toFixed(2) + " %"}
                            </Td>
                            <Td textAlign="center" bg="gray.50">
                              {((((24 - (currentPartNoData.investmentPlan?.nonWorkingTime ?? 0)) / 24) - ((24 - (currentPartNoData.recentActual?.nonWorkingTime ?? 0)) / 24)) * 100).toFixed(2) + " %"}
                            </Td>
                          </Tr>

                          {/* サイクルタイム */}
                          <Tr h="48px">
                            <Td borderRight="1px solid" borderColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.cycleTime')}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" w="100%" textAlign="center" isRequired isInvalid={!!currentPartNoErrors.recentActualCycleTime.isError}>
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={9999}
                                      precision={1}
                                      step={0.1}
                                      value={inputValues["recentActual.cycleTime"]}
                                      onChange={(value) => handleInputChange("recentActual", "cycleTime", value)}
                                      onBlur={() => handleInputBlur("recentActual", "cycleTime")}
                                    >
                                      <NumberInputField placeholder="sec" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper >
                                    </NumberInput>
                                    <FormErrorMessage display="flex" justifyContent="center">
                                      {currentPartNoErrors.recentActualCycleTime.errorMessage}
                                    </FormErrorMessage>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">sec</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" w="100%" textAlign="center" isRequired isInvalid={!!currentPartNoErrors.theoreticalCycleTime.isError}>
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={100000}
                                      precision={1}
                                      step={0.1}
                                      value={inputValues["theoretical.cycleTime"]}
                                      onChange={(value) => handleInputChange("theoretical", "cycleTime", value)}
                                      onBlur={() => handleInputBlur("theoretical", "cycleTime")}
                                    >
                                      <NumberInputField placeholder="sec" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                    <FormErrorMessage display="flex" justifyContent="center">
                                      {currentPartNoErrors.theoreticalCycleTime.errorMessage}
                                    </FormErrorMessage>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">sec</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" w="100%" textAlign="center" isRequired isInvalid={!!currentPartNoErrors.investmentPlanCycleTime.isError}>
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={100000}
                                      precision={1}
                                      step={0.1}
                                      value={inputValues["investmentPlan.cycleTime"]}
                                      onChange={(value) => handleInputChange("investmentPlan", "cycleTime", value)}
                                      onBlur={() => handleInputBlur("investmentPlan", "cycleTime")}
                                    >
                                      <NumberInputField placeholder="sec" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                    <FormErrorMessage display="flex" justifyContent="center">
                                      {currentPartNoErrors.investmentPlanCycleTime.errorMessage}
                                    </FormErrorMessage>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">sec</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td textAlign="center">
                              {safeNumber(
                                (currentPartNoData.investmentPlan?.cycleTime ?? 0) - (currentPartNoData.recentActual?.cycleTime ?? 0)
                              ).toFixed(1) + " sec"}
                            </Td>
                          </Tr>

                          {/* 1日あたり必要人員数 */}
                          <Tr h="48px">
                            <Td borderRight="1px solid" borderColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.personnelPerDay')}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" w="100%" textAlign="center" isRequired isInvalid={!!currentPartNoErrors.recentActualPersonnelPerDay.isError}>
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={9999999999}
                                      precision={1}
                                      step={0.1}
                                      value={inputValues["recentActual.personnelPerDay"]}
                                      onChange={(value) => handleInputChange("recentActual", "personnelPerDay", value)}
                                      onBlur={() => handleInputBlur("recentActual", "personnelPerDay")}
                                    >
                                      <NumberInputField placeholder="pax" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                    <FormErrorMessage display="flex" justifyContent="center">
                                      {currentPartNoErrors.recentActualPersonnelPerDay.errorMessage}
                                    </FormErrorMessage>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">pax</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" w="100%" textAlign="center" isRequired isInvalid={!!currentPartNoErrors.theoreticalPersonnelPerDay.isError}>
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={100000}
                                      precision={1}
                                      step={0.1}
                                      value={inputValues["theoretical.personnelPerDay"]}
                                      onChange={(value) => handleInputChange("theoretical", "personnelPerDay", value)}
                                      onBlur={() => handleInputBlur("theoretical", "personnelPerDay")}
                                    >
                                      <NumberInputField placeholder="pax" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                    <FormErrorMessage display="flex" justifyContent="center">
                                      {currentPartNoErrors.theoreticalPersonnelPerDay.errorMessage}
                                    </FormErrorMessage>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">pax</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" w="100%" textAlign="center" isRequired isInvalid={!!currentPartNoErrors.investmentPlanPersonnelPerDay.isError}>
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={100000}
                                      precision={1}
                                      step={0.1}
                                      value={inputValues["investmentPlan.personnelPerDay"]}
                                      onChange={(value) => handleInputChange("investmentPlan", "personnelPerDay", value)}
                                      onBlur={() => handleInputBlur("investmentPlan", "personnelPerDay")}
                                    >
                                      <NumberInputField placeholder="pax" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                    <FormErrorMessage display="flex" justifyContent="center">
                                      {currentPartNoErrors.investmentPlanPersonnelPerDay.errorMessage}
                                    </FormErrorMessage>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">pax</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td textAlign="center">
                              {safeNumber(
                                (currentPartNoData.investmentPlan?.personnelPerDay ?? 0) - (currentPartNoData.recentActual?.personnelPerDay ?? 0)
                              ).toFixed(1) + " pax"}
                            </Td>
                          </Tr>

                          {/* 生産能力 */}
                          <Tr bg="gray.50" h="48px">
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <HStack spacing={1}>
                                <Text>{t('components.UpdateSummaryInputPopover.tableHeader.productionVolume')}</Text>
                                <Popover placement="auto">
                                  <PopoverTrigger>
                                    <Button
                                      style={{ background: 'none', border: 'none', padding: 0 }}
                                      minW="auto"
                                      h="auto"
                                      ml={1}
                                    >
                                      <FaRegQuestionCircle size="16px" cursor="pointer" />
                                    </Button>
                                  </PopoverTrigger>
                                  <Portal>
                                    <PopoverContent width="fit-content" bgColor="#fffff0">
                                      <PopoverArrow />
                                      <PopoverBody>
                                        <Text fontSize="sm">{t('components.UpdateSummaryInputPopover.formula.productionVolume')}</Text>
                                      </PopoverBody>
                                    </PopoverContent>
                                  </Portal>
                                </Popover>
                              </HStack>
                            </Td>

                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                              {formatNumberLocal(
                                Math.round(
                                  safeNumber(
                                    (currentPartNoData.recentActual?.cycleTime ?? 0) > 0
                                      ? ((localUpdateSummary.operatingDays ?? 0) * 24 * 3600) / (currentPartNoData.recentActual?.cycleTime ?? 1) * ((24 - (currentPartNoData.recentActual?.nonWorkingTime ?? 0)) / 24) * ((currentPartNoData.recentActual?.yield ?? 0) / 100)
                                      : 0
                                  )
                                )
                              ) + " pcs"}
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                              {formatNumberLocal(
                                Math.round(
                                  safeNumber(
                                    (currentPartNoData.theoretical?.cycleTime ?? 0) > 0
                                      ? ((localUpdateSummary.operatingDays ?? 0) * 24 * 3600) / (currentPartNoData.theoretical?.cycleTime ?? 1) * ((24 - (currentPartNoData.theoretical?.nonWorkingTime ?? 0)) / 24) * ((currentPartNoData.theoretical?.yield ?? 0) / 100)
                                      : 0
                                  )
                                )
                              ) + " pcs"}
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center" bg="gray.50">
                              {formatNumberLocal(
                                Math.round(
                                  safeNumber(
                                    (currentPartNoData.investmentPlan?.cycleTime ?? 0) > 0
                                      ? ((localUpdateSummary.operatingDays ?? 0) * 24 * 3600) / (currentPartNoData.investmentPlan?.cycleTime ?? 1) * ((24 - (currentPartNoData.investmentPlan?.nonWorkingTime ?? 0)) / 24) * ((currentPartNoData.investmentPlan?.yield ?? 0) / 100)
                                      : 0
                                  )
                                )
                              ) + " pcs"}
                            </Td>
                            <Td textAlign="center" bg="gray.50">
                              {formatNumberLocal(
                                Math.round(
                                  safeNumber(
                                    ((currentPartNoData.investmentPlan?.cycleTime ?? 0) > 0
                                      ? ((localUpdateSummary.operatingDays ?? 0) * 24 * 3600) / (currentPartNoData.investmentPlan?.cycleTime ?? 1) * ((24 - (currentPartNoData.investmentPlan?.nonWorkingTime ?? 0)) / 24) * ((currentPartNoData.investmentPlan?.yield ?? 0) / 100)
                                      : 0) -
                                    ((currentPartNoData.recentActual?.cycleTime ?? 0) > 0
                                      ? ((localUpdateSummary.operatingDays ?? 0) * 24 * 3600) / (currentPartNoData.recentActual?.cycleTime ?? 1) * ((24 - (currentPartNoData.recentActual?.nonWorkingTime ?? 0)) / 24) * ((currentPartNoData.recentActual?.yield ?? 0) / 100)
                                      : 0)
                                  )
                                )
                              ) + " pcs"}
                            </Td>
                          </Tr>

                          {/* 設備あたり生産性 */}
                          <Tr bg="gray.50" h="48px">
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <HStack spacing={1}>
                                <Text>{t('components.UpdateSummaryInputPopover.tableHeader.productionPerEquipment')}</Text>
                                <Popover placement="auto">
                                  <PopoverTrigger>
                                    <Button
                                      style={{ background: 'none', border: 'none', padding: 0 }}
                                      minW="auto"
                                      h="auto"
                                      ml={1}
                                    >
                                      <FaRegQuestionCircle size="16px" cursor="pointer" />
                                    </Button>
                                  </PopoverTrigger>
                                  <Portal>
                                    <PopoverContent width="fit-content" bgColor="#fffff0">
                                      <PopoverArrow />
                                      <PopoverBody>
                                        <Text fontSize="sm">{t('components.UpdateSummaryInputPopover.formula.productionPerEquipment')}</Text>
                                      </PopoverBody>
                                    </PopoverContent>
                                  </Portal>
                                </Popover>
                              </HStack>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                              {safeNumber(
                                (currentPartNoData.recentActual?.cycleTime ?? 0) > 0 && (localUpdateSummary.operatingDays ?? 0) > 0
                                  ? (((localUpdateSummary.operatingDays ?? 0) * 24 * 3600) / (currentPartNoData.recentActual?.cycleTime ?? 1) * ((24 - (currentPartNoData.recentActual?.nonWorkingTime ?? 0)) / 24) * ((currentPartNoData.recentActual?.yield ?? 0) / 100)) / (localUpdateSummary.operatingDays ?? 1) / 24
                                  : 0
                              ).toFixed(2) + " pcs"}
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                              {safeNumber(
                                (currentPartNoData.theoretical?.cycleTime ?? 0) > 0 && (localUpdateSummary.operatingDays ?? 0) > 0
                                  ? (((localUpdateSummary.operatingDays ?? 0) * 24 * 3600) / (currentPartNoData.theoretical?.cycleTime ?? 1) * ((24 - (currentPartNoData.theoretical?.nonWorkingTime ?? 0)) / 24) * ((currentPartNoData.theoretical?.yield ?? 0) / 100)) / (localUpdateSummary.operatingDays ?? 1) / 24
                                  : 0
                              ).toFixed(2) + " pcs"}
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center" bg="gray.50">
                              {safeNumber(
                                (currentPartNoData.investmentPlan?.cycleTime ?? 0) > 0 && (localUpdateSummary.operatingDays ?? 0) > 0
                                  ? (((localUpdateSummary.operatingDays ?? 0) * 24 * 3600) / (currentPartNoData.investmentPlan?.cycleTime ?? 1) * ((24 - (currentPartNoData.investmentPlan?.nonWorkingTime ?? 0)) / 24) * ((currentPartNoData.investmentPlan?.yield ?? 0) / 100)) / (localUpdateSummary.operatingDays ?? 1) / 24
                                  : 0
                              ).toFixed(2) + " pcs"}
                            </Td>
                            <Td textAlign="center" bg="gray.50">
                              {safeNumber(
                                ((currentPartNoData.investmentPlan?.cycleTime ?? 0) > 0 && (localUpdateSummary.operatingDays ?? 0) > 0
                                  ? (((localUpdateSummary.operatingDays ?? 0) * 24 * 3600) / (currentPartNoData.investmentPlan?.cycleTime ?? 1) * ((24 - (currentPartNoData.investmentPlan?.nonWorkingTime ?? 0)) / 24) * ((currentPartNoData.investmentPlan?.yield ?? 0) / 100)) / (localUpdateSummary.operatingDays ?? 1) / 24
                                  : 0) -
                                ((currentPartNoData.recentActual?.cycleTime ?? 0) > 0 && (localUpdateSummary.operatingDays ?? 0) > 0
                                  ? (((localUpdateSummary.operatingDays ?? 0) * 24 * 3600) / (currentPartNoData.recentActual?.cycleTime ?? 1) * ((24 - (currentPartNoData.recentActual?.nonWorkingTime ?? 0)) / 24) * ((currentPartNoData.recentActual?.yield ?? 0) / 100)) / (localUpdateSummary.operatingDays ?? 1) / 24
                                  : 0)
                              ).toFixed(2) + " pcs"}
                            </Td>
                          </Tr>

                          {/* 1人あたり生産性 */}
                          <Tr bg="gray.50" h="48px">
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <HStack spacing={1}>
                                <Text>{t('components.UpdateSummaryInputPopover.tableHeader.productionPerPerson')}</Text>
                                <Popover placement="auto">
                                  <PopoverTrigger>
                                    <Button
                                      style={{ background: 'none', border: 'none', padding: 0 }}
                                      minW="auto"
                                      h="auto"
                                      ml={1}
                                    >
                                      <FaRegQuestionCircle size="16px" cursor="pointer" />
                                    </Button>
                                  </PopoverTrigger>
                                  <Portal>
                                    <PopoverContent width="fit-content" bgColor="#fffff0">
                                      <PopoverArrow />
                                      <PopoverBody>
                                        <Text fontSize="sm">{t('components.UpdateSummaryInputPopover.formula.productionPerPerson')}</Text>
                                      </PopoverBody>
                                    </PopoverContent>
                                  </Portal>
                                </Popover>
                              </HStack>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                              {safeNumber(
                                (currentPartNoData.recentActual?.cycleTime ?? 0) > 0 && (localUpdateSummary.operatingDays ?? 0) > 0 && Number(localUpdateSummary.shiftSystem) > 0 && (currentPartNoData.recentActual?.personnelPerDay ?? 0) > 0
                                  ? ((((localUpdateSummary.operatingDays ?? 0) * 24 * 3600) / (currentPartNoData.recentActual?.cycleTime ?? 1) * ((24 - (currentPartNoData.recentActual?.nonWorkingTime ?? 0)) / 24) * ((currentPartNoData.recentActual?.yield ?? 0) / 100)) / (localUpdateSummary.operatingDays ?? 1) / (Number(localUpdateSummary.shiftSystem) * (hourMinutesStringToMinutes(localUpdateSummary.workTime) / 60))) / (Number(localUpdateSummary.shiftSystem) * (currentPartNoData.recentActual?.personnelPerDay ?? 1))
                                  : 0
                              ).toFixed(2) + " pcs"}
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                              {safeNumber(
                                (currentPartNoData.theoretical?.cycleTime ?? 0) > 0 && (localUpdateSummary.operatingDays ?? 0) > 0 && Number(localUpdateSummary.shiftSystem) > 0 && (currentPartNoData.theoretical?.personnelPerDay ?? 0) > 0
                                  ? ((((localUpdateSummary.operatingDays ?? 0) * 24 * 3600) / (currentPartNoData.theoretical?.cycleTime ?? 1) * ((24 - (currentPartNoData.theoretical?.nonWorkingTime ?? 0)) / 24) * ((currentPartNoData.theoretical?.yield ?? 0) / 100)) / (localUpdateSummary.operatingDays ?? 1) / (Number(localUpdateSummary.shiftSystem) * (hourMinutesStringToMinutes(localUpdateSummary.workTime) / 60))) / (Number(localUpdateSummary.shiftSystem) * (currentPartNoData.theoretical?.personnelPerDay ?? 1))
                                  : 0
                              ).toFixed(2) + " pcs"}
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center" bg="gray.50">
                              {safeNumber(
                                (currentPartNoData.investmentPlan?.cycleTime ?? 0) > 0 && (localUpdateSummary.operatingDays ?? 0) > 0 && Number(localUpdateSummary.shiftSystem) > 0 && (currentPartNoData.investmentPlan?.personnelPerDay ?? 0) > 0
                                  ? ((((localUpdateSummary.operatingDays ?? 0) * 24 * 3600) / (currentPartNoData.investmentPlan?.cycleTime ?? 1) * ((24 - (currentPartNoData.investmentPlan?.nonWorkingTime ?? 0)) / 24) * ((currentPartNoData.investmentPlan?.yield ?? 0) / 100)) / (localUpdateSummary.operatingDays ?? 1) / (Number(localUpdateSummary.shiftSystem) * (hourMinutesStringToMinutes(localUpdateSummary.workTime) / 60))) / (Number(localUpdateSummary.shiftSystem) * (currentPartNoData.investmentPlan?.personnelPerDay ?? 1))
                                  : 0
                              ).toFixed(2) + " pcs"}
                            </Td>
                            <Td textAlign="center" bg="gray.50">
                              {safeNumber(
                                ((currentPartNoData.investmentPlan?.cycleTime ?? 0) > 0 && (localUpdateSummary.operatingDays ?? 0) > 0 && Number(localUpdateSummary.shiftSystem) > 0 && (currentPartNoData.investmentPlan?.personnelPerDay ?? 0) > 0
                                  ? ((((localUpdateSummary.operatingDays ?? 0) * 24 * 3600) / (currentPartNoData.investmentPlan?.cycleTime ?? 1) * ((24 - (currentPartNoData.investmentPlan?.nonWorkingTime ?? 0)) / 24) * ((currentPartNoData.investmentPlan?.yield ?? 0) / 100)) / (localUpdateSummary.operatingDays ?? 1) / (Number(localUpdateSummary.shiftSystem) * (hourMinutesStringToMinutes(localUpdateSummary.workTime) / 60))) / (Number(localUpdateSummary.shiftSystem) * (currentPartNoData.investmentPlan?.personnelPerDay ?? 1))
                                  : 0) -
                                ((currentPartNoData.recentActual?.cycleTime ?? 0) > 0 && (localUpdateSummary.operatingDays ?? 0) > 0 && Number(localUpdateSummary.shiftSystem) > 0 && (currentPartNoData.recentActual?.personnelPerDay ?? 0) > 0
                                  ? ((((localUpdateSummary.operatingDays ?? 0) * 24 * 3600) / (currentPartNoData.recentActual?.cycleTime ?? 1) * ((24 - (currentPartNoData.recentActual?.nonWorkingTime ?? 0)) / 24) * ((currentPartNoData.recentActual?.yield ?? 0) / 100)) / (localUpdateSummary.operatingDays ?? 1) / (Number(localUpdateSummary.shiftSystem) * (hourMinutesStringToMinutes(localUpdateSummary.workTime) / 60))) / (Number(localUpdateSummary.shiftSystem) * (currentPartNoData.recentActual?.personnelPerDay ?? 1))
                                  : 0)
                              ).toFixed(2) + " pcs"}
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    )}
                    {/* 注意書き（*、**） */}
                    {localUpdateSummary.selectedPartNo && (
                      <Box mt={2} fontSize="xs" color="gray.600">
                        <Text textAlign="left">{t('components.UpdateSummaryInputPopover.note.productionBasis')}</Text>
                      </Box>
                    )}
                  </fieldset>
                </Box>
              </Box>

              {/* フリー入力フィールド */}
              <fieldset disabled={!pagePermissions?.isEditable} style={!pagePermissions?.isEditable ? { pointerEvents: 'none' } : undefined}>
                <Box>
                  <Text fontWeight="bold" mb={3}>{t('components.UpdateSummaryInputPopover.section.analysis')}</Text>
                  <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4} fontSize="sm">
                    <HStack align="flex-start" spacing={4}>
                      {/* 左側の3項目 */}
                      <VStack align="stretch" spacing={2} flex={1}>
                        <FormControl isRequired isInvalid={!!errors.updateReason.isError}>
                          <FormLabel fontSize="sm">{t('components.UpdateSummaryInputPopover.label.updateReason')}</FormLabel>
                          <Textarea
                            size="sm"
                            value={localUpdateSummary.updateReason || ""}
                            onChange={(e) => handleTextFieldUpdate("updateReason", e.target.value)}
                            rows={3}
                            placeholder={t('components.UpdateSummaryInputPopover.placeholder.updateReason')}
                          />
                          <FormErrorMessage>{errors.updateReason.errorMessage}</FormErrorMessage>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.productionPerEquipment.isError}>
                          <FormLabel fontSize="sm">{t('components.UpdateSummaryInputPopover.label.productionPerEquipment')}</FormLabel>
                          <Textarea
                            size="sm"
                            value={localUpdateSummary.productionPerEquipment || ""}
                            onChange={(e) => handleTextFieldUpdate("productionPerEquipment", e.target.value)}
                            rows={3}
                            placeholder={t('components.UpdateSummaryInputPopover.placeholder.productionPerEquipment')}
                          />
                          <FormErrorMessage>{errors.productionPerEquipment.errorMessage}</FormErrorMessage>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.productionPerPerson.isError}>
                          <FormLabel fontSize="sm">{t('components.UpdateSummaryInputPopover.label.productionPerPerson')}</FormLabel>
                          <Textarea
                            size="sm"
                            value={localUpdateSummary.productionPerPerson || ""}
                            onChange={(e) => handleTextFieldUpdate("productionPerPerson", e.target.value)}
                            rows={3}
                            placeholder={t('components.UpdateSummaryInputPopover.placeholder.productionPerPerson')}
                          />
                          <FormErrorMessage>{errors.productionPerPerson.errorMessage}</FormErrorMessage>
                        </FormControl>
                      </VStack>

                      {/* 右側の3項目 */}
                      <VStack align="stretch" spacing={2} flex={1}>
                        <FormControl isRequired isInvalid={!!errors.nonOperationMeasures.isError}>
                          <FormLabel fontSize="sm">{t('components.UpdateSummaryInputPopover.label.nonOperationMeasures')}</FormLabel>
                          <Textarea
                            size="sm"
                            value={localUpdateSummary.nonOperationMeasures || ""}
                            onChange={(e) => handleTextFieldUpdate("nonOperationMeasures", e.target.value)}
                            rows={3}
                            placeholder={t('components.UpdateSummaryInputPopover.placeholder.nonOperationMeasures')}
                          />
                          <FormErrorMessage>{errors.nonOperationMeasures.errorMessage}</FormErrorMessage>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.nonProcessingMeasures.isError}>
                          <FormLabel fontSize="sm">{t('components.UpdateSummaryInputPopover.label.nonProcessingMeasures')}</FormLabel>
                          <Textarea
                            size="sm"
                            value={localUpdateSummary.nonProcessingMeasures || ""}
                            onChange={(e) => handleTextFieldUpdate("nonProcessingMeasures", e.target.value)}
                            rows={3}
                            placeholder={t('components.UpdateSummaryInputPopover.placeholder.nonProcessingMeasures')}
                          />
                          <FormErrorMessage>{errors.nonProcessingMeasures.errorMessage}</FormErrorMessage>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.currentEquipmentHandling.isError}>
                          <FormLabel fontSize="sm">{t('components.UpdateSummaryInputPopover.label.currentEquipmentHandling')}</FormLabel>
                          <Textarea
                            size="sm"
                            value={localUpdateSummary.currentEquipmentHandling || ""}
                            onChange={(e) => handleTextFieldUpdate("currentEquipmentHandling", e.target.value)}
                            rows={3}
                            placeholder={t('components.UpdateSummaryInputPopover.placeholder.currentEquipmentHandling')}
                          />
                          <FormErrorMessage>{errors.currentEquipmentHandling.errorMessage}</FormErrorMessage>
                        </FormControl>
                      </VStack>
                    </HStack>
                  </Box>
                </Box>

                {/* 損益セクション（詳細モードのみ） */}
                {!isSimpleMode && (
                  <Box>
                    <Text fontWeight="bold" mb={3}>{t('components.UpdateSummaryInputPopover.section.profitLoss')}</Text>
                    <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4} fontSize="sm">
                      <Table variant="simple" size="sm" border="1px solid" borderColor="gray.200" sx={{
                        tableLayout: "fixed",
                        borderCollapse: "collapse",
                        "& td, & th": {
                          borderBottom: "1px solid",
                          borderBottomColor: "gray.200"
                        }
                      }}>
                        <colgroup>
                          <col style={{ width: "150px" }} />
                          <col style={{ width: "150px" }} />
                          <col style={{ width: "150px" }} />
                          <col style={{ width: "150px" }} />
                          <col style={{ width: "150px" }} />
                        </colgroup>
                        <Thead bg="gray.100">
                          <Tr>
                            <Th rowSpan={2} borderRight="1px solid" borderColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.item')}</Th>
                            <Th colSpan={2} textAlign="center" borderRight="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.current')}</Th>
                            <Th colSpan={1} textAlign="center" borderRight="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.investmentUpdate')}</Th>
                            <Th colSpan={1} textAlign="center" borderRight="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.difference')}</Th>
                          </Tr>
                          <Tr>
                            <Th borderRight="1px solid" borderColor="gray.200" textAlign="center">{t('components.UpdateSummaryInputPopover.tableHeader.recentThreeMonthAverage')}</Th>
                            <Th borderRight="1px solid" borderColor="gray.200" textAlign="center">{t('components.UpdateSummaryInputPopover.tableHeader.initialPlan')}</Th>
                            <Th borderRight="1px solid" borderColor="gray.200" textAlign="center">{t('components.UpdateSummaryInputPopover.tableHeader.plan')}</Th>
                            <Th borderRight="1px solid" borderColor="gray.200" textAlign="center">{t('components.UpdateSummaryInputPopover.tableHeader.actualVsPlan')}</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {/* 生産数 */}
                          <Tr h="48px">
                            <Td borderRight="1px solid" borderColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.productionQuantity')}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center" isRequired={!isSimpleMode} isInvalid={!!errors.productionQuantity?.isError}>
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={9999999999}
                                      value={inputValues["profitLoss.productionQuantity"]}
                                      onChange={(value) => handleProductionQuantityInputChange(value)}
                                      onBlur={() => handleProductionQuantityInputBlur()}
                                    >
                                      <NumberInputField placeholder="pcs" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                    <FormErrorMessage>{errors.productionQuantity?.errorMessage}</FormErrorMessage>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">pcs</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                              {formatNumberLocal(localUpdateSummary.profitLoss?.productionQuantity ?? 0) + " pcs"}
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                              {formatNumberLocal(localUpdateSummary.profitLoss?.productionQuantity ?? 0) + " pcs"}
                            </Td>
                            <Td textAlign="center">
                              {formatNumberLocal(0) + " pcs"}
                            </Td>
                          </Tr>
                          {/* スクラップ率 */}
                          <Tr bg="gray.50" h="48px">
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <HStack spacing={1}>
                                <Text>{t('components.UpdateSummaryInputPopover.tableHeader.scrapRate')}</Text>
                                <Popover placement="auto">
                                  <PopoverTrigger>
                                    <Button
                                      style={{ background: 'none', border: 'none', padding: 0 }}
                                      minW="auto"
                                      h="auto"
                                      ml={1}
                                    >
                                      <FaRegQuestionCircle size="16px" cursor="pointer" />
                                    </Button>
                                  </PopoverTrigger>
                                  <Portal>
                                    <PopoverContent width="fit-content" bgColor="#fffff0">
                                      <PopoverArrow />
                                      <PopoverBody>
                                        <Text fontSize="sm">{t('components.UpdateSummaryInputPopover.formula.scrapRate')}</Text>
                                      </PopoverBody>
                                    </PopoverContent>
                                  </Portal>
                                </Popover>
                              </HStack>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                              {(100 - (currentPartNoData.recentActual?.yield ?? 0)).toFixed(2) + " %"}
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                              {(100 - (currentPartNoData.theoretical?.yield ?? 0)).toFixed(2) + " %"}
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center" bg="gray.50">
                              {(100 - (currentPartNoData.investmentPlan?.yield ?? 0)).toFixed(2) + " %"}
                            </Td>
                            <Td textAlign="center" bg="gray.50">
                              {((100 - (currentPartNoData.investmentPlan?.yield ?? 0)) - (100 - (currentPartNoData.recentActual?.yield ?? 0))).toFixed(2) + " %"}
                            </Td>
                          </Tr>
                          {/* スクラップ費用 */}
                          <Tr bg="gray.50" h="48px">
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <HStack spacing={1}>
                                <Text>{t('components.UpdateSummaryInputPopover.tableHeader.scrapCost')}</Text>
                                <Popover placement="auto">
                                  <PopoverTrigger>
                                    <Button
                                      style={{ background: 'none', border: 'none', padding: 0 }}
                                      minW="auto"
                                      h="auto"
                                      ml={1}
                                    >
                                      <FaRegQuestionCircle size="16px" cursor="pointer" />
                                    </Button>
                                  </PopoverTrigger>
                                  <Portal>
                                    <PopoverContent width="fit-content" bgColor="#fffff0">
                                      <PopoverArrow />
                                      <PopoverBody>
                                        <Text fontSize="sm">{t('components.UpdateSummaryInputPopover.formula.scrapCost')}</Text>
                                      </PopoverBody>
                                    </PopoverContent>
                                  </Portal>
                                </Popover>
                              </HStack>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                              {formatNumberLocal(
                                Math.round(
                                  (localUpdateSummary.profitLoss?.productionQuantity ?? 0) *
                                  ((100 - (currentPartNoData.recentActual?.yield ?? 0)) / 100) *
                                  (localUpdateSummary.cumulativeCost ?? 0)
                                )
                              ) + " JPY"}
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                              {formatNumberLocal(
                                Math.round(
                                  (localUpdateSummary.profitLoss?.productionQuantity ?? 0) *
                                  ((100 - (currentPartNoData.theoretical?.yield ?? 0)) / 100) *
                                  (localUpdateSummary.cumulativeCost ?? 0)
                                )
                              ) + " JPY"}
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                              {formatNumberLocal(
                                Math.round(
                                  (localUpdateSummary.profitLoss?.productionQuantity ?? 0) *
                                  ((100 - (currentPartNoData.investmentPlan?.yield ?? 0)) / 100) *
                                  (localUpdateSummary.cumulativeCost ?? 0)
                                )
                              ) + " JPY"}
                            </Td>
                            <Td textAlign="center" bg="gray.50">
                              {formatNumberLocal(
                                Math.round(
                                  (localUpdateSummary.profitLoss?.productionQuantity ?? 0) *
                                  ((100 - (currentPartNoData.investmentPlan?.yield ?? 0)) / 100) *
                                  (localUpdateSummary.cumulativeCost ?? 0)
                                ) -
                                Math.round(
                                  (localUpdateSummary.profitLoss?.productionQuantity ?? 0) *
                                  ((100 - (currentPartNoData.recentActual?.yield ?? 0)) / 100) *
                                  (localUpdateSummary.cumulativeCost ?? 0)
                                )
                              ) + " JPY"}
                            </Td>
                          </Tr>
                          {/* 修繕費 */}
                          <Tr h="48px">
                            <Td borderRight="1px solid" borderColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.repairCost')}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center">
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={9999999999}
                                      value={inputValues["profitLoss.recentActual.repairCost"]}
                                      onChange={(value) => handleProfitLossInputChange("recentActual", "repairCost", value)}
                                      onBlur={() => handleProfitLossInputBlur("recentActual", "repairCost")}
                                    >
                                      <NumberInputField placeholder="JPY" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">JPY</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center">
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={9999999999}
                                      value={inputValues["profitLoss.theoretical.repairCost"]}
                                      onChange={(value) => handleProfitLossInputChange("theoretical", "repairCost", value)}
                                      onBlur={() => handleProfitLossInputBlur("theoretical", "repairCost")}
                                    >
                                      <NumberInputField placeholder="JPY" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">JPY</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center">
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={9999999999}
                                      value={inputValues["profitLoss.investmentPlan.repairCost"]}
                                      onChange={(value) => handleProfitLossInputChange("investmentPlan", "repairCost", value)}
                                      onBlur={() => handleProfitLossInputBlur("investmentPlan", "repairCost")}
                                    >
                                      <NumberInputField placeholder="JPY" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">JPY</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td textAlign="center">
                              {formatNumberLocal(
                                (localUpdateSummary.profitLoss?.investmentPlan?.repairCost ?? 0) -
                                (localUpdateSummary.profitLoss?.recentActual?.repairCost ?? 0)
                              ) + " JPY"}
                            </Td>
                          </Tr>
                          {/* 消耗品費 */}
                          <Tr h="48px">
                            <Td borderRight="1px solid" borderColor="gray.200">{t('components.UpdateSummaryInputPopover.tableHeader.consumablesCost')}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center">
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={9999999999}
                                      value={inputValues["profitLoss.recentActual.consumablesCost"]}
                                      onChange={(value) => handleProfitLossInputChange("recentActual", "consumablesCost", value)}
                                      onBlur={() => handleProfitLossInputBlur("recentActual", "consumablesCost")}
                                    >
                                      <NumberInputField placeholder="JPY" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">JPY</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center">
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={9999999999}
                                      value={inputValues["profitLoss.theoretical.consumablesCost"]}
                                      onChange={(value) => handleProfitLossInputChange("theoretical", "consumablesCost", value)}
                                      onBlur={() => handleProfitLossInputBlur("theoretical", "consumablesCost")}
                                    >
                                      <NumberInputField placeholder="JPY" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">JPY</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td borderRight="1px solid" borderColor="gray.200">
                              <Box display="flex" justifyContent="center">
                                <HStack spacing={1}>
                                  <FormControl maxW="120px" textAlign="center">
                                    <NumberInput
                                      size="sm"
                                      width="120px"
                                      min={0}
                                      max={9999999999}
                                      value={inputValues["profitLoss.investmentPlan.consumablesCost"]}
                                      onChange={(value) => handleProfitLossInputChange("investmentPlan", "consumablesCost", value)}
                                      onBlur={() => handleProfitLossInputBlur("investmentPlan", "consumablesCost")}
                                    >
                                      <NumberInputField placeholder="JPY" textAlign="right" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  </FormControl>
                                  <Text w="50px" fontSize="xs">JPY</Text>
                                </HStack>
                              </Box>
                            </Td>
                            <Td textAlign="center">
                              {formatNumberLocal(
                                (localUpdateSummary.profitLoss?.investmentPlan?.consumablesCost ?? 0) -
                                (localUpdateSummary.profitLoss?.recentActual?.consumablesCost ?? 0)
                              ) + " JPY"}
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                      {/* 注意書き */}
                      <Box mt={2} fontSize="xs" color="gray.600">
                        <Text textAlign="left">{t('components.UpdateSummaryInputPopover.note.profitLossNote')}</Text>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* 指標グループ（簡易モードのみ） */}
                {isSimpleMode && (
                  <Box>
                    <Text fontWeight="bold" mb={3}>{t('components.UpdateSummaryInputPopover.section.indicator')}</Text>
                    <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4} fontSize="sm">
                      <HStack spacing={4} mb={4} alignItems="center">
                        <Text fontSize="sm" fontWeight="medium">{t('components.UpdateSummaryInputPopover.label.item')}</Text>
                        <Select
                          size="sm"
                          w="200px"
                          value={selectedIndicatorItemType}
                          onChange={(e) => handleItemTypeChange("indicator", e.target.value as RationalizationProfitLossItemType | "")}
                        >
                          {INDICATOR_ITEM_OPTIONS.filter((option) =>
                            option.value === "" || option.value === "custom" || !indicatorItems.some(item => item.itemType === option.value)
                          ).map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </Select>
                        <ReadOnlyExemptButton colorScheme="blue" onClick={() => handleGroupGoogleSpread("indicator")} isLoading={isGsLoading} isDisabled={indicatorItems.length === 0}>GoogleSpread</ReadOnlyExemptButton>
                        <Button colorScheme="blue" onClick={() => handleGroupDataLink("indicator")} isLoading={isGsLoading} isDisabled={!activeIndicatorSpreadsheetId || isIndicatorSpreadsheetWindowClosed}>{t('components.UpdateSummaryInputPopover.button.dataLink')}</Button>
                        <Popover placement="auto">
                          <PopoverTrigger>
                            <Button style={{ background: 'none', border: 'none', padding: 0 }} minW="auto" h="auto">
                              <FaRegQuestionCircle size="16px" cursor="pointer" />
                            </Button>
                          </PopoverTrigger>
                          <Portal>
                            <PopoverContent width="400px" bgColor="#fffff0">
                              <PopoverArrow />
                              <PopoverBody>
                                <VStack align="start" spacing={1} fontSize="sm">
                                  <UnorderedList>
                                    <ListItem>
                                      <Button colorScheme="blue" size="xs">{t('components.UpdateSummaryInputPopover.button.dataLink')}</Button>{t('components.UpdateSummaryInputPopover.help.dataLinkExcludeDescription')}
                                      <UnorderedList>
                                        <ListItem>{t('components.UpdateSummaryInputPopover.help.dataLinkExcludeNoItem')}</ListItem>
                                        <ListItem>{t('components.UpdateSummaryInputPopover.help.dataLinkExcludeInvalidKbn')}</ListItem>
                                        <ListItem>{t('components.UpdateSummaryInputPopover.help.dataLinkExcludeInvalidMonth')}</ListItem>
                                      </UnorderedList>
                                    </ListItem>
                                    <ListItem>{t('components.UpdateSummaryInputPopover.help.dataLinkItemErrorIndicator')}</ListItem>
                                    <ListItem>{t('components.UpdateSummaryInputPopover.help.dataLinkMonthError')}<Button colorScheme="blue" size="xs">{t('components.UpdateSummaryInputPopover.button.dataLink')}</Button>{t('components.UpdateSummaryInputPopover.help.dataLinkMonthErrorSuffix')}</ListItem>
                                  </UnorderedList>
                                </VStack>
                              </PopoverBody>
                            </PopoverContent>
                          </Portal>
                        </Popover>
                        <Text fontSize="xs" color="gray.600">{t('components.UpdateSummaryInputPopover.help.compareNote')}</Text>
                      </HStack>
                      {monthList.length === 0 && (
                        <Text color="orange.500" mb={4}>{t('components.UpdateSummaryInputPopover.message.noMonthWarning', { startDateFieldName: t('features.InvestmentScreening.BasicInfo.field.projectStartDate'), endDateFieldName: t('features.InvestmentScreening.BasicInfo.field.projectEndDate') })}</Text>
                      )}
                      {monthList.length > 0 && (
                        <Box position="relative" h="370px" border="1px solid #ccc">
                          <Box position="absolute" top={0} left={0} right={0} bottom={0} overflowX="auto" overflowY="auto">
                            <Table variant="simple" size="sm" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} className="mm-ceas-table-extend">
                              <colgroup>
                                <col width={`${WIDTH_ITEM_NAME}px`} />
                                <col width={`${WIDTH_OPERATE}px`} />
                                <col width={`${WIDTH_KBN}px`} />
                                {monthList.map((month) => <col key={month} width={`${WIDTH_VALUE}px`} />)}
                              </colgroup>
                              <Thead position="sticky" top={0} bg="white" zIndex="10">
                                <Tr>
                                  <Th position="sticky" left={`${getFixedColumnLeft(1)}px`} zIndex="10">{t('components.UpdateSummaryInputPopover.label.item')}</Th>
                                  <Th position="sticky" left={`${getFixedColumnLeft(2)}px`} zIndex="10">{t('components.UpdateSummaryInputPopover.label.operate')}</Th>
                                  <Th position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="10">{t('components.UpdateSummaryInputPopover.label.kbn')}</Th>
                                  {monthList.map((month) => <Th key={month}>{formatMonthDisplay(month)}</Th>)}
                                </Tr>
                              </Thead>
                              <Tbody>
                                {indicatorItems.map((item, indicatorIndex) => (
                                  <React.Fragment key={item.id}>
                                    <Tr h="40px" onMouseEnter={() => setHoveredIndicatorItemId(item.id)} onMouseLeave={() => setHoveredIndicatorItemId(null)}>
                                      <Td position="sticky" left={`${getFixedColumnLeft(1)}px`} zIndex="1" rowSpan={2} verticalAlign="middle"
                                        boxShadow={indicatorErrorCells.has(`${item.id}:item`) ? "inset 0 0 0 2px #E53E3E" : undefined}
                                        style={{ background: hoveredIndicatorItemId === item.id ? "#eee" : "#fff" }}>
                                        <HStack spacing={1} align="center">
                                          {item.itemType === "custom" ? (
                                            <Text as="span" cursor={!pagePermissions?.isEditable ? "default" : "pointer"}
                                              color={!pagePermissions?.isEditable ? "black" : "blue.600"}
                                              textDecoration={!pagePermissions?.isEditable ? "none" : "underline"}
                                              _hover={{ color: "blue.800" }}
                                              onClick={() => { if (!pagePermissions?.isEditable) return; handleEditCustomItem("indicator", item); }}>
                                              {getItemDisplayName(item)}
                                            </Text>
                                          ) : (
                                            <Text as="span">{getItemDisplayName(item)}</Text>
                                          )}
                                          {requiredIndicatorItemTypes.includes(item.itemType) && (
                                            <Badge colorScheme="red" variant="subtle">{t('components.UpdateSummaryInputPopover.badge.required')}</Badge>
                                          )}
                                        </HStack>
                                      </Td>
                                      <Td position="sticky" left={`${getFixedColumnLeft(2)}px`} zIndex="1" rowSpan={2} verticalAlign="middle"
                                        style={{ background: hoveredIndicatorItemId === item.id ? "#eee" : "#fff" }}>
                                        <VStack spacing={0}>
                                          <Button size="xs" variant="ghost" onClick={() => moveItem("indicator", item.id, "up")} isDisabled={indicatorIndex === 0} title={t('common.button.moveUp')}><FiChevronUp /></Button>
                                          <Button size="xs" variant="ghost" onClick={() => moveItem("indicator", item.id, "down")} isDisabled={indicatorIndex === indicatorItems.length - 1} title={t('common.button.moveDown')}><FiChevronDown /></Button>
                                          {!requiredIndicatorItemTypes.includes(item.itemType) && (
                                            <Button size="xs" colorScheme="red" variant="ghost" onClick={() => removeItem("indicator", item.id)} title={t('common.button.delete')}><FiTrash2 /></Button>
                                          )}
                                        </VStack>
                                      </Td>
                                      <Td position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="1" style={{ background: hoveredIndicatorItemId === item.id ? "#eee" : "#fff" }}>
                                        {t('components.UpdateSummaryInputPopover.label.beforeUpdate')}
                                      </Td>
                                      {monthList.map((month) => (
                                        <Td key={month} textAlign="right"
                                          boxShadow={indicatorErrorCells.has(`${item.id}:${month}:before`) ? "inset 0 0 0 2px #E53E3E" : undefined}
                                          style={{ background: hoveredIndicatorItemId === item.id ? "#eee" : "#fff" }}>
                                          {formatNumber(item.beforeMonthlyData[month] ?? 0, "", 0)} {getItemUnit(item, "indicator")}
                                        </Td>
                                      ))}
                                    </Tr>
                                    <Tr h="40px" onMouseEnter={() => setHoveredIndicatorItemId(item.id)} onMouseLeave={() => setHoveredIndicatorItemId(null)}>
                                      <Td position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="1" style={{ background: hoveredIndicatorItemId === item.id ? "#eee" : "#fff" }}>
                                        {t('components.UpdateSummaryInputPopover.label.afterUpdate')}
                                      </Td>
                                      {monthList.map((month) => (
                                        <Td key={month} textAlign="right"
                                          boxShadow={indicatorErrorCells.has(`${item.id}:${month}:after`) ? "inset 0 0 0 2px #E53E3E" : undefined}
                                          style={{ background: hoveredIndicatorItemId === item.id ? "#eee" : "#fff" }}>
                                          {formatNumber(item.afterMonthlyData[month] ?? 0, "", 0)} {getItemUnit(item, "indicator")}
                                        </Td>
                                      ))}
                                    </Tr>
                                  </React.Fragment>
                                ))}
                                {indicatorItems.length === 0 && (
                                  <Tr><Td colSpan={3 + monthList.length} textAlign="center" color="gray.500">{t('components.UpdateSummaryInputPopover.message.addIndicatorItem')}</Td></Tr>
                                )}
                              </Tbody>
                            </Table>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* 費用グループ（簡易モードのみ） */}
                {isSimpleMode && (
                  <Box>
                    <Text fontWeight="bold" mb={3}>{t('components.UpdateSummaryInputPopover.section.cost')}</Text>
                    <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4} fontSize="sm">
                      <HStack spacing={4} mb={4} alignItems="center" flexWrap="wrap">
                        <Text fontSize="sm" fontWeight="medium">{t('components.UpdateSummaryInputPopover.label.item')}</Text>
                        <Select
                          size="sm"
                          w="200px"
                          value={selectedCostItemType}
                          onChange={(e) => handleItemTypeChange("cost", e.target.value as RationalizationProfitLossItemType | "")}
                        >
                          {COST_ITEM_OPTIONS.filter((option) =>
                            option.value === "" || option.value === "custom" || !costItems.some(item => item.itemType === option.value)
                          ).map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </Select>
                        <ReadOnlyExemptButton colorScheme="blue" onClick={() => handleGroupGoogleSpread("cost")} isLoading={isGsLoading} isDisabled={costItems.length === 0}>GoogleSpread</ReadOnlyExemptButton>
                        <Button colorScheme="blue" onClick={() => handleGroupDataLink("cost")} isLoading={isGsLoading} isDisabled={!activeCostSpreadsheetId || isCostSpreadsheetWindowClosed}>{t('components.UpdateSummaryInputPopover.button.dataLink')}</Button>
                        <Popover placement="auto">
                          <PopoverTrigger>
                            <Button style={{ background: 'none', border: 'none', padding: 0 }} minW="auto" h="auto">
                              <FaRegQuestionCircle size="16px" cursor="pointer" />
                            </Button>
                          </PopoverTrigger>
                          <Portal>
                            <PopoverContent width="400px" bgColor="#fffff0">
                              <PopoverArrow />
                              <PopoverBody>
                                <VStack align="start" spacing={1} fontSize="sm">
                                  <UnorderedList>
                                    <ListItem>
                                      <Button colorScheme="blue" size="xs">{t('components.UpdateSummaryInputPopover.button.dataLink')}</Button>{t('components.UpdateSummaryInputPopover.help.dataLinkExcludeDescription')}
                                      <UnorderedList>
                                        <ListItem>{t('components.UpdateSummaryInputPopover.help.dataLinkExcludeNoItem')}</ListItem>
                                        <ListItem>{t('components.UpdateSummaryInputPopover.help.dataLinkExcludeInvalidKbn')}</ListItem>
                                        <ListItem>{t('components.UpdateSummaryInputPopover.help.dataLinkExcludeInvalidMonth')}</ListItem>
                                      </UnorderedList>
                                    </ListItem>
                                    <ListItem>{t('components.UpdateSummaryInputPopover.help.dataLinkItemErrorCost')}</ListItem>
                                    <ListItem>{t('components.UpdateSummaryInputPopover.help.dataLinkMonthError')}<Button colorScheme="blue" size="xs">{t('components.UpdateSummaryInputPopover.button.dataLink')}</Button>{t('components.UpdateSummaryInputPopover.help.dataLinkMonthErrorSuffix')}</ListItem>
                                  </UnorderedList>
                                </VStack>
                              </PopoverBody>
                            </PopoverContent>
                          </Portal>
                        </Popover>
                        <Text fontSize="sm" fontWeight="medium" ml={4}>{t('components.UpdateSummaryInputPopover.label.currency')}</Text>
                        <Box>
                          <FormControl w="120px" isRequired={isSimpleMode && costItems.length > 0} isInvalid={!!errors.costCurrency.isError}>
                            <Select
                              size="sm"
                              value={selectedCostCurrency}
                              onChange={(e) => {
                                const newCurrencyId = e.target.value;
                                const targetItem = costItems.find(item => item.itemType === "targetEquipmentDepreciation");
                                const updateBefore = !targetItem?.isManuallyEditedBefore;
                                const updateAfter = !targetItem?.isManuallyEditedAfter;
                                if ((updateBefore || updateAfter) && equipmentCacheRef.current) {
                                  calcDepreciationFromCache(newCurrencyId, equipmentCacheRef.current, { updateBefore, updateAfter });
                                } else if (updateBefore || updateAfter) {
                                  calcTargetEquipmentDepreciation({ updateBefore, updateAfter, targetCurrency: newCurrencyId }).catch(err => console.error("償却費自動計算エラー:", err));
                                }
                                setSelectedCostCurrency(newCurrencyId);
                              }}
                            >
                              <option value="">{t('components.UpdateSummaryInputPopover.option.selectPlaceholder')}</option>
                              {currencyData.map((currency) => (
                                <option key={currency.currencyId} value={currency.currencyId}>{currency.currencyName}</option>
                              ))}
                            </Select>
                            <FormErrorMessage fontSize="xs">{errors.costCurrency.errorMessage}</FormErrorMessage>
                          </FormControl>
                        </Box>
                        <Text fontSize="xs" color="gray.600">{t('components.UpdateSummaryInputPopover.help.compareNote')}</Text>
                      </HStack>
                      {monthList.length === 0 && (
                        <Text color="orange.500" mb={4}>{t('components.UpdateSummaryInputPopover.message.noMonthWarning', { startDateFieldName: t('features.InvestmentScreening.BasicInfo.field.projectStartDate'), endDateFieldName: t('features.InvestmentScreening.BasicInfo.field.projectEndDate') })}</Text>
                      )}
                      {monthList.length > 0 && (
                        <Box position="relative" h="370px" border="1px solid #ccc">
                          <Box position="absolute" top={0} left={0} right={0} bottom={0} overflowX="auto" overflowY="auto">
                            <Table variant="simple" size="sm" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} className="mm-ceas-table-extend">
                              <colgroup>
                                <col width={`${WIDTH_ITEM_NAME}px`} />
                                <col width={`${WIDTH_OPERATE}px`} />
                                <col width={`${WIDTH_KBN}px`} />
                                {monthList.map((month) => <col key={month} width={`${WIDTH_VALUE}px`} />)}
                              </colgroup>
                              <Thead position="sticky" top={0} bg="white" zIndex="10">
                                <Tr>
                                  <Th position="sticky" left={`${getFixedColumnLeft(1)}px`} zIndex="10">{t('components.UpdateSummaryInputPopover.label.item')}</Th>
                                  <Th position="sticky" left={`${getFixedColumnLeft(2)}px`} zIndex="10">{t('components.UpdateSummaryInputPopover.label.operate')}</Th>
                                  <Th position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="10">{t('components.UpdateSummaryInputPopover.label.kbn')}</Th>
                                  {monthList.map((month) => <Th key={month}>{formatMonthDisplay(month)}</Th>)}
                                </Tr>
                              </Thead>
                              <Tbody>
                                {costItems.map((item, costIndex) => (
                                  <React.Fragment key={item.id}>
                                    <Tr h="40px" onMouseEnter={() => setHoveredCostItemId(item.id)} onMouseLeave={() => setHoveredCostItemId(null)}>
                                      <Td position="sticky" left={`${getFixedColumnLeft(1)}px`} zIndex="1" rowSpan={2} verticalAlign="middle"
                                        style={{ background: hoveredCostItemId === item.id ? "#eee" : "#fff" }}>
                                        <HStack spacing={1} align="center">
                                          {item.itemType === "custom" ? (
                                            <Text as="span" cursor={!pagePermissions?.isEditable ? "default" : "pointer"}
                                              color={!pagePermissions?.isEditable ? "black" : "blue.600"}
                                              textDecoration={!pagePermissions?.isEditable ? "none" : "underline"}
                                              _hover={{ color: "blue.800" }}
                                              onClick={() => { if (!pagePermissions?.isEditable) return; handleEditCustomItem("cost", item); }}>
                                              {getItemDisplayName(item)}
                                            </Text>
                                          ) : (
                                            <Text as="span">{getItemDisplayName(item)}</Text>
                                          )}
                                          {requiredCostItemTypes.includes(item.itemType) && (
                                            <Badge colorScheme="red" variant="subtle">{t('components.UpdateSummaryInputPopover.badge.required')}</Badge>
                                          )}
                                        </HStack>
                                        {item.itemType === "targetEquipmentDepreciation" && (
                                          <VStack mt={1} spacing="2px" align="flex-start">
                                            <HStack spacing={1}>
                                              <Badge colorScheme={item.isManuallyEditedBefore ? "blue" : "gray"} fontSize="10px">
                                                {item.isManuallyEditedBefore
                                                  ? t('components.UpdateSummaryInputPopover.badge.manuallyEditedBefore')
                                                  : t('components.UpdateSummaryInputPopover.badge.autoCalculatedBefore')}
                                              </Badge>
                                              {pagePermissions?.isEditable && item.isManuallyEditedBefore && (
                                                <Button size="xs" variant="link" colorScheme="blue" fontSize="10px"
                                                  onClick={() => calcTargetEquipmentDepreciation({ updateBefore: true, updateAfter: false })}>
                                                  {t('components.UpdateSummaryInputPopover.badge.resetToAutoCalc')}
                                                </Button>
                                              )}
                                            </HStack>
                                            <HStack spacing={1}>
                                              <Badge colorScheme={item.isManuallyEditedAfter ? "blue" : "gray"} fontSize="10px">
                                                {item.isManuallyEditedAfter
                                                  ? t('components.UpdateSummaryInputPopover.badge.manuallyEditedAfter')
                                                  : t('components.UpdateSummaryInputPopover.badge.autoCalculatedAfter')}
                                              </Badge>
                                              {pagePermissions?.isEditable && item.isManuallyEditedAfter && (
                                                <Button size="xs" variant="link" colorScheme="blue" fontSize="10px"
                                                  onClick={() => calcTargetEquipmentDepreciation({ updateBefore: false, updateAfter: true })}>
                                                  {t('components.UpdateSummaryInputPopover.badge.resetToAutoCalc')}
                                                </Button>
                                              )}
                                            </HStack>
                                          </VStack>
                                        )}
                                      </Td>
                                      <Td position="sticky" left={`${getFixedColumnLeft(2)}px`} zIndex="1" rowSpan={2} verticalAlign="middle"
                                        style={{ background: hoveredCostItemId === item.id ? "#eee" : "#fff" }}>
                                        <VStack spacing={0}>
                                          <Button size="xs" variant="ghost" onClick={() => moveItem("cost", item.id, "up")} isDisabled={costIndex === 0} title={t('common.button.moveUp')}><FiChevronUp /></Button>
                                          <Button size="xs" variant="ghost" onClick={() => moveItem("cost", item.id, "down")} isDisabled={costIndex === costItems.length - 1} title={t('common.button.moveDown')}><FiChevronDown /></Button>
                                          {!requiredCostItemTypes.includes(item.itemType) && (
                                            <Button size="xs" colorScheme="red" variant="ghost" onClick={() => removeItem("cost", item.id)} title={t('common.button.delete')}><FiTrash2 /></Button>
                                          )}
                                        </VStack>
                                      </Td>
                                      <Td position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="1" style={{ background: hoveredCostItemId === item.id ? "#eee" : "#fff" }}>
                                        {t('components.UpdateSummaryInputPopover.label.beforeUpdate')}
                                      </Td>
                                      {monthList.map((month) => (
                                        <Td key={month} textAlign="right"
                                          boxShadow={costErrorCells.has(`${item.id}:${month}:before`) ? "inset 0 0 0 2px #E53E3E" : undefined}
                                          style={{ background: hoveredCostItemId === item.id ? "#eee" : "#fff" }}>
                                          {formatNumber(item.beforeMonthlyData[month] ?? 0, getItemUnit(item, "cost"))} {getItemUnit(item, "cost")}
                                        </Td>
                                      ))}
                                    </Tr>
                                    <Tr h="40px" onMouseEnter={() => setHoveredCostItemId(item.id)} onMouseLeave={() => setHoveredCostItemId(null)}>
                                      <Td position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="1" style={{ background: hoveredCostItemId === item.id ? "#eee" : "#fff" }}>
                                        {t('components.UpdateSummaryInputPopover.label.afterUpdate')}
                                      </Td>
                                      {monthList.map((month) => (
                                        <Td key={month} textAlign="right"
                                          boxShadow={costErrorCells.has(`${item.id}:${month}:after`) ? "inset 0 0 0 2px #E53E3E" : undefined}
                                          style={{ background: hoveredCostItemId === item.id ? "#eee" : "#fff" }}>
                                          {formatNumber(item.afterMonthlyData[month] ?? 0, getItemUnit(item, "cost"))} {getItemUnit(item, "cost")}
                                        </Td>
                                      ))}
                                    </Tr>
                                  </React.Fragment>
                                ))}
                                {costItems.length === 0 && (
                                  <Tr><Td colSpan={3 + monthList.length} textAlign="center" color="gray.500">{t('components.UpdateSummaryInputPopover.message.addCostItem')}</Td></Tr>
                                )}
                              </Tbody>
                            </Table>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </fieldset>

            </VStack>
          </ModalBody>
          <ModalFooter flexDirection="column" gap={3} alignItems="stretch">
            {isAIEnabled && !isAIRestricted && (
              <Box
                bg={isAIAdviceConfirmed ? "green.50" : "purple.50"}
                border="1px solid"
                borderColor={isAIAdviceConfirmed ? "green.200" : "purple.200"}
                borderRadius="md"
                px={4}
                py={3}
              >
                <VStack align="stretch" spacing={3}>
                  {/* タイトル */}
                  <HStack>
                    <RiSparklingFill color="#805AD5" />
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">{t('components.UpdateSummaryInputPopover.label.aiAnalysis')}</Text>
                  </HStack>

                  {/* ボタンと実行状態 */}
                  <HStack spacing={4}>
                    <VStack spacing={1} align="center">
                      <Button
                        onClick={() => setIsAIModalOpen(true)}
                        leftIcon={<RiSparklingFill />}
                        variant="solid"
                        colorScheme="purple"
                        size="sm"
                      >
                        {t('components.UpdateSummaryInputPopover.button.equipmentLifecycleAnalysis')}
                      </Button>
                      <Text fontSize="xs" color={isAIAnalyzed ? "green.500" : "gray.400"} fontWeight="bold">
                        {isAIAnalyzed ? t('components.UpdateSummaryInputPopover.aiStatus.executed') : t('components.UpdateSummaryInputPopover.aiStatus.notExecuted')}
                      </Text>
                    </VStack>
                    <VStack spacing={1} align="center">
                      <Button
                        onClick={() => setIsKnowledgeModalOpen(true)}
                        leftIcon={<RiSparklingFill />}
                        variant="solid"
                        colorScheme="teal"
                        size="sm"
                      >
                        {t('components.UpdateSummaryInputPopover.button.manufacturingKnowledgeSearch')}
                      </Button>
                      <Text fontSize="xs" color={isAIKnowledgeAnalyzed ? "green.500" : "gray.400"} fontWeight="bold">
                        {isAIKnowledgeAnalyzed ? t('components.UpdateSummaryInputPopover.aiStatus.executed') : t('components.UpdateSummaryInputPopover.aiStatus.notExecuted')}
                      </Text>
                    </VStack>
                  </HStack>

                  {/* 最終確認チェックボックス */}
                  <Checkbox
                    isChecked={isAIAdviceConfirmed}
                    isDisabled={!isAIAnalyzed || !isAIKnowledgeAnalyzed}
                    onChange={(e) => setIsAIAdviceConfirmed(e.target.checked)}
                    size="md"
                    colorScheme="green"
                  >
                    <Text fontSize="sm" fontWeight="bold" color={isAIAnalyzed && isAIKnowledgeAnalyzed ? "gray.700" : "gray.400"}>
                      {t('components.UpdateSummaryInputPopover.label.aiAdviceConfirm')}
                    </Text>
                  </Checkbox>
                </VStack>
              </Box>
            )}
            <HStack justifyContent="flex-end">
              {pagePermissions?.isEditable && (
                <>
                  <Button colorScheme="teal" variant="outline" onClick={handleTempSave}>{t('components.UpdateSummaryInputPopover.button.tempSave')}</Button>
                  <Button colorScheme="teal" variant="outline" onClick={handleTempLoad}>{t('components.UpdateSummaryInputPopover.button.tempLoad')}</Button>
                </>
              )}
              <Button colorScheme="blue"
                isDisabled={isSaving || (isAIEnabled && !isAIRestricted && !isAIAdviceConfirmed) || !pagePermissions?.isEditable}
                onClick={() => {
                  if (validate() === false) {
                    toast({
                      title: TITLE,
                      description: t('components.UpdateSummaryInputPopover.toast.inputError'),
                      status: 'warning',
                      duration: 2000,
                      isClosable: true,
                    });
                    return;
                  }
                  handleSave();
                }}>
                {t('components.UpdateSummaryInputPopover.button.save')}
              </Button>
              <Button onClick={handleReset} ml={2}>
                {t('components.UpdateSummaryInputPopover.button.close')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 自由入力項目モーダル */}
      <Modal isOpen={isCustomItemModalOpen} onClose={handleCustomItemCancel} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('components.UpdateSummaryInputPopover.modal.customItemTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">{t('components.UpdateSummaryInputPopover.label.itemName')}</FormLabel>
                <Input
                  size="sm"
                  value={customItemName}
                  onChange={(e) => setCustomItemName(e.target.value)}
                  placeholder={t('components.UpdateSummaryInputPopover.placeholder.itemName')}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">{t('components.UpdateSummaryInputPopover.label.unit')}</FormLabel>
                <Input
                  size="sm"
                  value={customUnit}
                  onChange={(e) => setCustomUnit(e.target.value)}
                  placeholder={t('components.UpdateSummaryInputPopover.placeholder.unit')}
                  isReadOnly={customItemTargetGroup === "cost"}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleCustomItemConfirm} isDisabled={!customItemName.trim() || !customUnit.trim()}>
              {t('common.button.confirm')}
            </Button>
            <Button onClick={handleCustomItemCancel}>{t('common.button.cancel')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* AI分析モーダル（設備寿命・更新分析） */}
      <AIAnalysisModal
        isAIModalOpen={isAIModalOpen}
        setIsAIModalOpen={(open: boolean) => {
          setIsAIModalOpen(open);
          // モーダルを閉じた時にフラグをセット
          if (!open) setIsAIAnalyzed(true);
        }}
        title={AI_ANALYSIS_TITLES.UPDATE_SUMMARY}
        projectId={projectId}
        initialPrompt={aiPrompt}
      />

      {/* AI分析モーダル（製造ナレッジ検索） */}
      <AIAnalysisModal
        isAIModalOpen={isKnowledgeModalOpen}
        setIsAIModalOpen={(open: boolean) => {
          setIsKnowledgeModalOpen(open);
          // モーダルを閉じた時にフラグをセット
          if (!open) setIsAIKnowledgeAnalyzed(true);
        }}
        title={AI_ANALYSIS_TITLES.MANUFACTURING_KNOWLEDGE}
        projectId={projectId}
        initialPrompt={knowledgePrompt}
      />
    </>
  );
};

export default UpdateSummaryInputPopover;
